/*
 * panels.js
 *
 * Every `buildX` function from the original artifact, ported to ES
 * modules. Each one returns an HTML string that gets injected into
 * `#p-{id}` by render.js / switchTab().
 *
 * Why string templates instead of DOM construction? It matches the
 * original code closely, makes diffs against florijn_v4.html easy to
 * verify, and is plenty fast at this scale. If forms-losing-focus on
 * re-render becomes a problem later, this is the place to introduce
 * targeted DOM updates.
 */

import { state } from './state.js';
import { weeklyRemainingFor } from './helpers.js';
import { ma, freqM, curKey, todayKey, hist, prevHist, isNow, histFor,
         catById, catsForScope, homeSegs, samenSegs, inkomstenMaand,
         vastLoon, spaarbedrag, inkomstenSegs, surplusVorigeMaandState,
         weekBudgetFor, monthBudgetFor, spentThisWeek, spentInCycle,
         spentPerItem, sinkingFundFor, cycleKeyToMonthInput,
         catsOverMaandbudget, catsOverPlanned,
         bespaarStatus, expensiveItems, themeRecolor, accentColor, accentSamenColor,
         spaarWithdrawalStatus, samenBorrowingStatus,
         samenSchulden, runway, doelProjectie, abonnementenJaar,
         noodfondsDroom, noodfondsStatus, noodfondsDoelSuggestie,
         externeSchuldenTotaal, externeSchuldenMaandMin, avalancheVolgorde,
         onverwachtTrend, cashflowProjectie, ankerDay, voorgesteldSaldo,
         pensioensparenStatus,
         spaarquoteJaar, spaarQuote, reflectiePendingCycleKey, abonnsForReflection,
         loonPendingCycleKey,
         droomWeight, effectivePct, deadlineMonthsLeft, deadlineMaandbedrag,
         getActiveTheme, isAutoPrideActive,
         nameFor } from './helpers.js';
import { MONTHS, SUBTABS_P, SUBTABS_S } from './constants.js';
import { icon } from './icons.js';
import { readFabSide } from './lib/fab-pref.js';

/**
 * Wrap een rij-HTML in een swipe-container met links/rechts dezelfde actie-set.
 *  @param {string} frontHTML  De zichtbare rij-content (irow of txrow).
 *  @param {object} actions    { edit: 'onclick="..."'-string, del: idem }.
 *                             Laat een veld leeg om die actie weg te laten.
 */
function wrapSwipe(frontHTML, actions = {}) {
  const editBtn = actions.edit
    ? `<button class="swipe-act swipe-edit" title="Bewerken" onclick="${actions.edit}">${icon('edit', 'color:#fff', 18)}</button>`
    : '';
  const pauseBtn = actions.pause
    ? `<button class="swipe-act swipe-pause" title="${actions.pauseLabel || 'Pauze'}" onclick="${actions.pause}"><span style="font-size:18px">${actions.pauseIcon || '⏸'}</span></button>`
    : '';
  const delBtn = actions.del
    ? `<button class="swipe-act swipe-del" title="Verwijderen" onclick="${actions.del}">${icon('trash', 'color:#fff', 18)}</button>`
    : '';
  const set = editBtn + pauseBtn + delBtn;
  return `<div class="swipe-wrap">
    <div class="swipe-actions">
      <div class="swipe-side swipe-side-l">${set}</div>
      <div class="swipe-side swipe-side-r">${set}</div>
    </div>
    <div class="swipe-front">${frontHTML}</div>
  </div>`;
}

// -----------------------------------------------------------------
// Meldingen — account-gebonden notif-bundeling
// -----------------------------------------------------------------

/**
 * Verzamel alle actuele meldingen voor één persoon. Returns { items, count }
 * waar items[] losse notif-HTML-strings zijn (om er bvb. te kunnen tellen,
 * sorteren of partial-render te doen) en count = items.length.
 *
 * Wordt aangeroepen door buildMeldingen() (= het meldingen-tabblad) én door
 * de bell-badge teller in render.js. Werkt altijd voor "vandaag" — bevat
 * geen isNow()/curKey()-guard zodat de teller niet leeg lijkt wanneer de
 * gebruiker een vorige maand bekijkt.
 */
export function gatherNotifs(person) {
  if (!person || person === 'samen') return { items: [], count: 0 };
  const d = state.S[person];
  if (!d) return { items: [], count: 0 };

  const items = [];
  const cKey = todayKey(person);

  // 1. Cashflow-alarm (rode notif) — saldo zakt onder €0.
  const cf = cashflowProjectie(person, 60);
  if (cf && cf.goesNegative) {
    const dStr = cf.minDate.getDate() + ' ' + MONTHS[cf.minDate.getMonth()].toLowerCase();
    items.push(`<div class="notif nf-red">
      <div class="ntitle">⚠️ Saldo zakt onder €0 op ${dStr}</div>
      <div class="nsub">Verwacht laagste saldo: €${cf.minBalance.toFixed(0)}. Tijd om vaste lasten te bekijken of saldo bij te werken.</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="openSettings()">Saldo bijwerken</button>
        <button class="nbtn"    onclick="closeNotifs();switchSubTab('vast')">Naar vaste kosten</button>
      </div>
    </div>`);
  }

  // 2. Loon ontvangen? — gated cyclus-overgang. Verschijnt op de anker-dag
  //     van de loon-inkomst. Zolang de gebruiker niet bevestigt blijft de
  //     vorige cyclus actief (= todayKey is gated, zie helpers.js).
  const loonPendingKey = loonPendingCycleKey(person);
  if (loonPendingKey) {
    const [lY, lM] = loonPendingKey.split('-').map(Number);
    const lblNieuw = MONTHS[Math.max(0, Math.min(11, lM))] + ' ' + lY;
    items.push(`<div class="notif nf-blue">
      <div class="ntitle">💼 Loon ontvangen?</div>
      <div class="nsub">Bevestig om de nieuwe budget-cyclus (${lblNieuw}) te starten. Nog niet? Dan blijven we met het loon van vorige maand werken.</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="loonOntvangen('ja')">Ja — nieuwe maand starten</button>
        <button class="nbtn"    onclick="loonOntvangen('nee')">Nog niet</button>
      </div>
    </div>`);
  }

  // 3. Gem-storting — bij elke nieuwe cyclus weer vragen, tenzij auto.
  //    Bedrag komt uit het auto-item dat refreshAutoItems() net heeft
  //    bijgewerkt (= helft van samen vast+var/mnd). Zonder samen-kosten
  //    is dat 0 — dan slaan we de melding helemaal over, anders zou een
  //    verse user een nutteloze "stort €0,00"-vraag krijgen.
  const gemDone    = d.gemHandled?.[cKey];
  const gemAandeel = (d.vast || []).find(it => it.auto && it.cat === 'gezamenlijk')?.a || 0;
  // Notif alleen tonen als er ook effectief een gezamenlijke rekening is —
  // anders heeft "al gestort?" geen betekenis (settings → 🏠 Huishouden).
  if (!gemDone && gemAandeel > 0 && state.hasSharedAccount) {
    items.push(`<div class="notif nf-green">
      <div class="ntitle">💶 Al gestort naar de gemeenschappelijke rekening?</div>
      <div class="nsub">Maandelijkse bijdrage: €${gemAandeel.toFixed(2)}</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="doGem('ja')">Ja, gedaan!</button>
        <button class="nbtn" onclick="doGem('auto')">Vanaf nu automatisch</button>
      </div>
    </div>`);
  }

  // 3. Surplus van vorige cyclus — vraag of bevestiging.
  const sur = surplusVorigeMaandState(person);
  if (sur?.question) {
    items.push(`<div class="notif nf-blue">
      <div class="ntitle">💸 €${sur.question.toFixed(2)} bleef over van vorige loon</div>
      <div class="nsub">Sparen of bij dit maand zetten?</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="surplusKies('spaar')">Naar spaardoelen</button>
        <button class="nbtn"    onclick="surplusKies('mee')">Bij dit loon</button>
        <button class="nbtn"    onclick="surplusKies('negeer')">Negeren</button>
      </div>
    </div>`);
  }

  // 4. Spaarstoring (20% van vast loon).
  const spaarDone = d.spaarHandled?.[cKey];
  const spaarAmt = spaarbedrag(person);
  if (spaarAmt > 0 && !spaarDone) {
    items.push(`<div class="notif nf-amber">
      <div class="ntitle">🐷 Heb je al €${spaarAmt.toFixed(2)} gespaard?</div>
      <div class="nsub">20% van je vast loon — wordt verdeeld over je spaardoelen</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="doSpaar('ja')">Ja, gestort!</button>
        <button class="nbtn" onclick="doSpaar('auto')">Vanaf nu automatisch</button>
        <button class="nbtn nbtn-soft" onclick="openSpaarSkip()">Deze maand niet</button>
      </div>
    </div>`);
  }

  // 5. Spaarpot-gebruik te vaak deze cyclus.
  const sw = spaarWithdrawalStatus(person);
  if (sw?.tooMuch) {
    items.push(`<div class="notif nf-amber">
      <div class="ntitle">⚠️ Je gebruikt je spaarpot vaak deze cyclus</div>
      <div class="nsub">${sw.count} keer · totaal €${sw.total.toFixed(2)} uit je dromen gehaald. Misschien tijd om je budget te bekijken?</div>
    </div>`);
  }

  // 6. Samen-leen te vaak deze cyclus.
  const sbw = samenBorrowingStatus(person);
  if (sbw?.tooMuch) {
    items.push(`<div class="notif nf-amber">
      <div class="ntitle">⚠️ Je leent vaak van de gemeenschappelijke rekening</div>
      <div class="nsub">${sbw.count} keer · totaal €${sbw.total.toFixed(2)} geleend deze cyclus. Misschien tijd om ergens op te besparen?</div>
    </div>`);
  }

  // 7. Bespaar-notif — vaste/var totalen over 50/30 target.
  const bs = bespaarStatus(person);
  if (bs.bespaarModus) {
    items.push(`<div class="notif nf-red">
      <div class="ntitle">⚠️ Vaste of variabele kosten zijn te hoog</div>
      <div class="nsub">
        ${bs.overVast ? `Vast €${bs.vastTotal.toFixed(0)} (over max €${bs.targetVast.toFixed(0)}, +€${bs.teveelVast.toFixed(0)})` : ''}
        ${bs.overVast && bs.overVar ? ' · ' : ''}
        ${bs.overVar  ? `Variabel €${bs.varTotal.toFixed(0)} (over max €${bs.targetVar.toFixed(0)}, +€${bs.teveelVar.toFixed(0)})` : ''}
      </div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="openBespaar()">Besparen — duurste items bekijken</button>
      </div>
    </div>`);
  }

  // 8. Onverwacht-trend (3 cycli op rij raak).
  const trend = onverwachtTrend(person);
  const ignoredKey = d.onverwachtTrendIgnored;
  if (trend.allAboveThreshold && ignoredKey !== cKey) {
    items.push(`<div class="notif nf-amber">
      <div class="ntitle">⚠️ 'Onverwacht' is geen onverwacht meer</div>
      <div class="nsub">Gemiddeld €${trend.avg.toFixed(0)}/maand de laatste 3 cycli. Wil je dit als vast item inplannen?</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="onverwachtNaarVast()">Vast item toevoegen</button>
        <button class="nbtn" onclick="onverwachtNegeer()">Negeer</button>
      </div>
    </div>`);
  }

  // 9. Maandbudget-overschrijdingen — weekly + planned. Per cat per cyclus
  //    dismissbaar via ackOverBudget — komt terug volgende cyclus als de
  //    overshoot opnieuw gebeurt; reflectie-overzicht negeert de ack zodat
  //    de maandelijkse review ze nog ziet.
  const curCK = curKey();
  const acked = state.S[person]?.cycleReviews?.[curCK]?.overBudgetAcked || {};
  const overWeek    = catsOverMaandbudget(person).filter(o => !acked[o.catId]);
  const overPlanned = catsOverPlanned(person)  .filter(o => !acked[o.catId]);
  overWeek.forEach(o => {
    const cm = catById(o.catId);
    items.push(`<div class="notif nf-red">
      <div class="ntitle">⚠️ ${cm.emoji} ${cm.label} — over maandbudget</div>
      <div class="nsub">€${o.spent.toFixed(2)} uitgegeven · doel €${o.budget.toFixed(0)} (= 4 × €${(o.budget/4).toFixed(0)}/wk)</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="ackOverBudget('${o.catId}','${curCK}')">Oké, dank</button>
      </div>
    </div>`);
  });
  overPlanned.forEach(o => {
    const cm = catById(o.catId);
    items.push(`<div class="notif nf-red">
      <div class="ntitle">⚠️ ${cm.emoji} ${cm.label} — over plan</div>
      <div class="nsub">€${o.spent.toFixed(2)} uitgegeven · gepland €${o.planned.toFixed(2)}/mnd (€${o.over.toFixed(2)} over)</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="ackOverBudget('${o.catId}','${curCK}')">Oké, dank</button>
      </div>
    </div>`);
  });

  // 10. Coaching — reflectie + abonnementen-check.
  const pendCk = reflectiePendingCycleKey(person);
  if (pendCk) {
    const [pY, pM] = pendCk.split('-').map(Number);
    const lblMaand = MONTHS[Math.max(0, Math.min(11, pM))] + ' ' + pY;
    items.push(`<div class="notif nf-blue">
      <div class="ntitle">📓 Vorige cyclus klaar (${lblMaand}) — 30 seconden reflectie?</div>
      <div class="nsub">Even stilstaan bij wat goed liep en wat je anders zou willen.</div>
      <div class="nbtns">
        <button class="nbtn ok" onclick="openReflect('${pendCk}')">Doe de reflectie</button>
        <button class="nbtn"    onclick="skipReflect('${pendCk}')">Sla over</button>
      </div>
    </div>`);
  }
  // 10b. Abonn-checks zitten sinds 2026-06-20 IN de reflectie-modal — niet
  //      meer als losse notif. Verlost het meldingen-paneel van de dagelijkse
  //      drempel; ze komen geconcentreerd terug bij maandafsluit (max 2).

  // 11. Bevestigings-strips (compacte groen-vinkje regels) onderaan.
  if (d.gemAuto && gemDone) {
    items.push(`<div class="notif nf-green" style="flex-direction:row;align-items:center;gap:8px">
      ${icon('check', 'color:' + accentColor(), 15)}
      <span style="font-size:13px;color:var(--color-text-secondary)">Gemeenschappelijk: automatisch actief</span>
    </div>`);
  }
  if (sur?.handled === 'spaar') {
    items.push(`<div class="notif nf-green" style="flex-direction:row;align-items:center;gap:8px">
      ${icon('check', 'color:' + accentColor(), 15)}
      <span style="font-size:13px;color:var(--color-text-secondary)">€${sur.amount.toFixed(2)} verdeeld over spaardoelen — bekijk Dromen</span>
    </div>`);
  } else if (sur?.handled === 'mee') {
    items.push(`<div class="notif nf-green" style="flex-direction:row;align-items:center;gap:8px">
      ${icon('check', 'color:' + accentColor(), 15)}
      <span style="font-size:13px;color:var(--color-text-secondary)">€${sur.amount.toFixed(2)} toegevoegd als "Rest vorige maand"</span>
    </div>`);
  }
  if (d.spaarAuto && spaarDone) {
    items.push(`<div class="notif nf-amber" style="flex-direction:row;align-items:center;gap:8px">
      ${icon('check', 'color:' + accentSamenColor(), 15)}
      <span style="font-size:13px;color:var(--color-text-secondary)">Sparen: automatisch actief — €${spaarAmt.toFixed(2)} verdeeld over dromen</span>
    </div>`);
  }

  return { items, count: items.length };
}

/**
 * Bell-badge teller: telt notifs die om aandacht vragen.
 *  - Rode alerts (nf-red) tellen altijd — urgent, ook zonder actieknop.
 *  - Notifs met actieknoppen (nbtns) tellen — er valt iets te doen.
 *  - Pure info-meldingen zonder rood + zonder knoppen (bv. spaarpot-misbruik,
 *    samen-lenen) tellen NIET — vroeger pompten die de teller op zonder dat
 *    er iets te doen viel.
 *  - Bevestigings-strips (zonder ntitle) tellen sowieso niet.
 */
export function notifBadgeCount(person) {
  const { items } = gatherNotifs(person);
  return items.filter(html =>
    html.includes('ntitle') && (html.includes('nf-red') || html.includes('nbtns'))
  ).length;
}

/** Zit er een rode alert in de notif-stack? Bepaalt of de bel-icoon zelf
 *  rood kleurt (extra-urgent signaal, niet enkel via badge-cijfer). */
export function notifHasRed(person) {
  const { items } = gatherNotifs(person);
  return items.some(html => html.includes('nf-red'));
}

/** Volledig meldingen-paneel voor de huidige user. */
export function buildMeldingen() {
  const { items, count } = gatherNotifs(state.user);
  const empty = `<div class="meldingen-empty">
    <div style="font-size:32px;margin-bottom:8px">✨</div>
    <div style="font-size:14px;color:var(--color-text-primary);font-weight:500">Geen meldingen</div>
    <div style="font-size:12px;color:var(--color-text-secondary);margin-top:4px">Alles staat in orde — niks te doen.</div>
  </div>`;
  return `<div class="meldingen-head">
      <span style="font-size:13px;color:var(--color-text-secondary)">Meldingen voor ${nameFor(state.user)}</span>
      <span style="font-size:12px;color:var(--color-text-secondary)">${count} ${count === 1 ? 'item' : 'items'}</span>
    </div>
    ${count === 0 ? empty : items.join('')}`;
}

// -----------------------------------------------------------------
// Home — KPI tiles + month nav + quick add + week budgets
// -----------------------------------------------------------------

/** Maand-tegel: klassieke "nog over deze maand" — inkomst − uitgaven. */
function buildMonthKpiTile(seg) {
  const color = seg.resterend < 0 ? '#c0392b' : accentColor();
  const label = seg.resterend < 0 ? 'Tekort deze maand' : 'Nog over deze maand';
  return `<div class="kpi-rest" style="border-color:${color}">
    <div class="kpi-rest-lbl">${label}</div>
    <div class="kpi-rest-amt" style="color:${color}">€${Math.abs(seg.resterend).toFixed(2)}</div>
    <div class="kpi-rest-sub">inkomen €${seg.inkomst.toFixed(0)} − uitgegeven €${seg.uitgegeven.toFixed(0)}</div>
  </div>`;
}

/** Week-tegel: weeklyShare − off-plan extras deze week (Ma-Zo). */
function buildWeekKpiTile() {
  const w = weeklyRemainingFor(state.person);
  const color = w.weeklyRemaining < 0 ? '#c0392b' : accentColor();
  const label = w.weeklyRemaining < 0 ? 'Tekort deze week' : 'Nog over deze week';
  return `<div class="kpi-rest" style="border-color:${color}">
    <div class="kpi-rest-lbl">${label}</div>
    <div class="kpi-rest-amt" style="color:${color}">€${Math.abs(w.weeklyRemaining).toFixed(2)}</div>
    <div class="kpi-rest-sub">weekbedrag €${w.weeklyShare.toFixed(0)} − deze week uitgegeven €${w.extras.toFixed(0)}</div>
  </div>`;
}

export function buildHome() {
  const d = state.S[state.person];
  const seg = homeSegs(state.person);

  // Pie-totaal = inkomen; legenda toont percentage van het inkomen
  // dat aan elke categorie ging. Resterend zit niet in de legenda —
  // de KPI bovenaan toont dat al, en de lege ring van de gauge ook.
  const totForPct = seg.inkomst > 0 ? seg.inkomst : (seg.uitgegeven || 1);
  const legend = themeRecolor(seg.cats).map(c => ({
    label: `${c.emoji} ${c.label}`,
    color: c.color,
    amount: c.amount,
    pct: Math.round(c.amount / totForPct * 100)
  }));

  // Notifs zijn naar het Meldingen-paneel verhuisd; Home blijft schoon.

  // Eerste-loon-hint: verse gebruiker zonder inkomsten ziet alles op €0
  // en weet niet waar te beginnen. Toon een groene welkomst-tip die wijst
  // naar de Inkomsten-tab. Alleen op eigen view (niet samen, niet kijker).
  const isOwnView = state.person === state.user && state.person !== 'samen';
  const hasIncomes = (state.S[state.person]?.inkomsten || []).length > 0;
  const loonHint = (isOwnView && !hasIncomes) ? `
    <div class="loon-hint">
      <div class="loon-hint-title">👋 Welkom! Eerste stap: voeg je loon toe</div>
      <div class="loon-hint-body">
        Zodra Florijn weet welk loon je krijgt en wanneer, kan 't je hele cyclus, runway en spaarquote berekenen. Ga naar <strong>Inkomsten</strong> → tik op + → kies "Loon" en zet "anker" aan.
      </div>
      <button class="loon-hint-btn" onclick="switchTabById('inkomsten')">→ Naar Inkomsten</button>
    </div>` : '';

  // KPI-tegel: nog over deze maand OF nog over deze week, afhankelijk van
  // de user-setting (👤 User → "Nog over"-weergave). Bij week-modus tonen
  // we weeklyShare − off-plan extras-van-deze-week; bij maand-modus de
  // klassieke resterend (inkomst − uitgegeven). Tekort = rood.
  const remView   = state.S.settings?.remainingView === 'week' ? 'week' : 'month';
  const kpiTile = (remView === 'week' && state.person !== 'samen')
    ? buildWeekKpiTile()
    : buildMonthKpiTile(seg);

  // Vaste-lasten-ratio + runway als kleine mini-tegels naast/onder de KPI.
  // Skip bij Samen of als er geen anker-inkomst is (ratio's hebben geen basis).
  let miniTiles = '';
  const loonVL = vastLoon(state.person);
  if (loonVL > 0) {
    const bsHome     = bespaarStatus(state.person);
    const vastTotalH = bsHome.vastTotal || 0;
    const targetVH   = bsHome.targetVast || 0;
    const ratioPct   = Math.round(vastTotalH / loonVL * 100);
    const ratioColor = bsHome.overVast ? '#c0392b' : 'var(--color-text-primary)';
    const iBtn = (id) => `<button class="info-btn" style="width:14px;height:14px;font-size:8px;margin-left:4px" title="Wat is dit?" onclick="toggleForm('${id}')">i</button>`;

    const vastTile = `<div class="kpi-mini"${bsHome.overVast ? ' style="border-color:#c0392b"' : ''}>
      <div class="kpi-mini-lbl">Vaste lasten ${iBtn('info-vastlasten')}</div>
      <div class="kpi-mini-amt" style="color:${ratioColor}">${ratioPct}% van loon</div>
      <div class="kpi-mini-sub">€${vastTotalH.toFixed(0)} / €${targetVH.toFixed(0)} max</div>
    </div>`;

    const rw = runway(state.person);
    const rwTxt = rw.months > 0
      ? rw.months.toFixed(1).replace('.', ',') + ' maanden'
      : (rw.vastTotal <= 0 ? '— geen vaste lasten' : '— stel noodfonds in');
    const rwSubLbl = rw.source === 'noodfonds' ? 'noodfonds' : 'gespaard';
    const runwayTile = `<div class="kpi-mini">
      <div class="kpi-mini-lbl">Runway ${iBtn('info-runway')}</div>
      <div class="kpi-mini-amt">${rwTxt}</div>
      <div class="kpi-mini-sub">€${rw.buffer.toFixed(0)} ${rwSubLbl} / €${vastTotalH.toFixed(0)}/mnd vast</div>
    </div>`;

    const vastInfo = `<div class="info-block" id="info-vastlasten" style="display:none">
      <strong>Vaste lasten</strong> = alles wat elke maand sowieso van je rekening gaat: huur, verzekeringen, abonnementen, vaste vervoerkosten. Dit getal toont welk percentage van je vast loon (€${loonVL.toFixed(0)}) daaraan opgaat.
      <br><br>
      <strong>Waarom dit telt:</strong> onder 50% heb je ademruimte voor onverwachte uitgaven en sparen. Boven 50% wordt elke tegenslag — een defect, een factuur, een korte ziekte — meteen pijnlijk omdat er geen marge is. Hoe lager dit getal, hoe vrijer je financieel staat. Vandaar dat het randje rood kleurt zodra je over de 50% gaat.
    </div>`;

    // Spaarquote: % van inkomen dat overblijft deze cyclus. Voor afgesloten
    // cyclus = effectief gespaard; voor huidige cyclus = "tot dusver".
    const sq = spaarQuote(state.person);
    let spaarTile = '';
    if (sq.pct !== null) {
      const sqColor = sq.pct >= 20 ? '#2a9d5a' : (sq.pct >= 10 ? 'var(--color-text-primary)' : '#c0392b');
      const sqLbl   = isNow() ? 'tot dusver' : 'gespaard';
      spaarTile = `<div class="kpi-mini">
        <div class="kpi-mini-lbl">Spaarquote ${iBtn('info-spaarquote')}</div>
        <div class="kpi-mini-amt" style="color:${sqColor}">${sq.pct.toFixed(0)}%</div>
        <div class="kpi-mini-sub">€${sq.gespaard.toFixed(0)} ${sqLbl} / €${sq.inkomst.toFixed(0)} in</div>
      </div>`;
    }

    const spaarInfo = sq.pct !== null ? `<div class="info-block" id="info-spaarquote" style="display:none">
      <strong>Spaarquote</strong> = welk percentage van je inkomen je deze cyclus overhoudt na alle uitgaven (vaste lasten, variabele kosten, abonnementen, gezamenlijke storting). Wat overblijft is wat je kunt sparen.
      <br><br>
      <strong>Waarom dit telt:</strong> dit is hét single getal dat financieel adviseurs hanteren voor je financiële gezondheid. Het maakt het verschil zichtbaar tussen "uitkomen" (0%) en "vooruitkomen" (10%+). Vuistregel: 20% is gezond, 10% is mager, boven 25% bouw je echt vermogen op.
      <br><br>
      <em>Let op:</em> in de lopende cyclus is dit een momentopname die nog daalt naarmate je meer uitgeeft. Pas na cycle-einde is het je effectieve spaarquote.
    </div>` : '';

    const runwayBron = rw.source === 'noodfonds'
      ? `<br><br><em>Bron:</em> alleen je noodfonds (€${rw.buffer.toFixed(0)}) telt mee — vakantie- en droomgeld blijft buiten beschouwing, dat wil je niet aanspreken bij jobverlies.`
      : `<br><br><em>Let op:</em> geen noodfonds gemarkeerd, dus dit telt nu álle dromen mee. Markeer er één als noodfonds in de Dromen-tab voor een zuivere meter.`;
    const runwayInfo = `<div class="info-block" id="info-runway" style="display:none">
      <strong>Runway</strong> = hoeveel maanden je kan blijven leven van wat je nu gespaard hebt als je morgen geen loon meer krijgt. Berekening: veiligheidsbuffer ÷ vaste lasten per maand.
      <br><br>
      <strong>Waarom dit telt:</strong> dit is je veiligheidsnet uitgedrukt in maanden, niet in euro's. €5.000 klinkt veel, maar bij €1.500 vaste lasten is dat 3,3 maanden ademruimte. Vuistregel: minstens 3 maanden, ideaal 6. Hoe langer je runway, hoe rustiger je tegen jobverlies, ziekte of een grote levensverandering kunt aankijken.${runwayBron}
    </div>`;

    miniTiles = `<div class="kpi-mini-row">${vastTile}${spaarTile}${runwayTile}</div>${vastInfo}${spaarInfo}${runwayInfo}`;
  }

  // Cashflow-prognose (Fase C). Eigen tegel onder de KPI-mini-rij want
  // 3 tegels op één rij is te krap op mobiel. Verborgen op Samen.
  // Het rode "saldo zakt onder €0"-alarm is verhuisd naar Meldingen.
  let cashflowBlock = '';
  if (state.person !== 'samen') {
    const cf = cashflowProjectie(state.person, 60);
    if (cf === null) {
      cashflowBlock = `<div style="font-size:12px;color:var(--color-text-secondary);text-align:center;padding:6px 0;margin-bottom:.85rem">
        Cashflow-prognose: stel je huidige saldo in via ⚙️ Instellingen.
      </div>`;
    } else {
      const dStr = cf.minDate.getDate() + ' ' + MONTHS[cf.minDate.getMonth()].toLowerCase();
      const upd  = state.S[state.person]?.huidigSaldoUpdated || '—';
      const minColor = cf.goesNegative ? '#c0392b' : 'var(--color-text-primary)';
      const cfiBtn = `<button class="info-btn" style="width:14px;height:14px;font-size:8px;margin-left:4px" title="Wat is dit?" onclick="toggleForm('info-cashflow')">i</button>`;
      cashflowBlock = `<div class="kpi-mini" style="margin-bottom:.85rem${cf.goesNegative ? ';border-color:#c0392b' : ''}">
        <div class="kpi-mini-lbl">Cashflow 60d ${cfiBtn}</div>
        <div class="kpi-mini-amt" style="color:${minColor}">Min: €${cf.minBalance.toFixed(0)} op ${dStr}</div>
        <div class="kpi-mini-sub">saldo nu €${cf.startSaldo.toFixed(0)} · bijgewerkt ${upd}</div>
      </div>
      <div class="info-block" id="info-cashflow" style="display:none">
        <strong>Cashflow 60d</strong> = projectie van je rekeningsaldo over de komende 60 dagen, op basis van geplande maandelijkse inkomsten en uitgaven (met betaaldag waar ingesteld, anders je anker-dag).
        <br><br>
        <strong>Min</strong> = het laagste verwachte saldo binnen dat venster, en op welke dag dat valt. Zakt het onder €0, krijg je een rode melding in de Meldingen-tab.
        <br><br>
        <strong>Wel meegerekend:</strong> de maandelijkse storting naar de gezamenlijke rekening, plus alle vaste én variabele items inclusief abonnementen met domiciliëring (alles wat automatisch van je rekening gaat).
        <br>
        <em>Niet meegerekend:</em> jaarlijkse posten (verzekeringen, wegentax) — die horen op de spaarpot via sinking funds, niet op de lopende rekening.
      </div>`;
    }
  }

  // Reflectie-herbekijken (Fase E). Past-cycle kaartje met note of oproep.
  let pastReflectCard = '';
  if (state.person !== 'samen' && !isNow()) {
    const cKeyP = curKey();
    const lblP = MONTHS[Math.max(0, Math.min(11, state.vM))] + ' ' + state.vY;
    const rev = (state.S[state.person]?.cycleReviews || {})[cKeyP];
    if (rev?.note) {
      const safeNote = rev.note.replace(/</g, '&lt;');
      pastReflectCard = `<div class="card" style="margin-bottom:.85rem;border-left:3px solid #378ADD">
        <div class="card-title" style="display:flex;justify-content:space-between;align-items:center">
          <span>📓 Reflectie — ${lblP}</span>
          <button class="addtoggle" onclick="openReflect('${cKeyP}')">Bewerken</button>
        </div>
        <div style="font-size:13px;color:var(--color-text-primary);white-space:pre-wrap;margin-top:4px">${safeNote}</div>
        <div style="font-size:11px;color:var(--color-text-secondary);margin-top:6px">Opgeslagen op ${rev.completedAt || '—'}</div>
      </div>`;
    } else if (rev?.skipped) {
      pastReflectCard = `<div style="font-size:12px;color:var(--color-text-secondary);text-align:center;padding:6px 0;margin-bottom:.85rem">
        📓 Reflectie ${lblP} overgeslagen ·
        <button class="addtoggle" style="margin-left:4px" onclick="openReflect('${cKeyP}')">Alsnog invullen</button>
      </div>`;
    } else {
      pastReflectCard = `<div style="font-size:12px;color:var(--color-text-secondary);text-align:center;padding:6px 0;margin-bottom:.85rem">
        📓 Geen reflectie voor ${lblP} ·
        <button class="addtoggle" style="margin-left:4px" onclick="openReflect('${cKeyP}')">+ Reflectie toevoegen</button>
      </div>`;
    }
  }

  return loonHint + pastReflectCard + buildSchuldenCard() + kpiTile + miniTiles + cashflowBlock + `
    <button class="add-open-btn" onclick="openAdd()">
      ${icon('plus', 'color:currentColor', 16)} Nieuwe transactie
    </button>

    <div class="month-nav">
      <button class="mnbtn" id="mprev" onclick="chMonth(-1)">‹ Vorige</button>
      <span style="font-size:13px;font-weight:500;color:var(--color-text-primary)">${MONTHS[state.vM]} ${state.vY}</span>
      <button class="mnbtn" id="mnext" onclick="chMonth(1)">Volgende ›</button>
    </div>

    <div class="card">
      <div class="card-title">Uitgaven per categorie</div>
      <div class="pie-wrap">
        <canvas id="pieHome" width="130" height="130"></canvas>
        <div class="pie-leg">
          ${legend.map(x => `
            <div class="lrow">
              <div class="ldot" style="background:${x.color}"></div>
              <span class="llbl">${x.label}</span>
              <span class="lpct">${x.pct}%</span>
            </div>`).join('') || '<div style="font-size:12px;color:var(--color-text-secondary)">Nog geen uitgaven deze maand.</div>'}
        </div>
      </div>
    </div>

    <div id="qa-confirm"></div>

    ${Object.keys(d.weekBudgets || {}).filter(c => d.weekBudgets[c] > 0).map(c => buildWW(c)).join('')}
  `;
}

// -----------------------------------------------------------------
// Schulden tegel — openstaande leningen tussen persoon en Samen
// -----------------------------------------------------------------

export function buildSchuldenCard() {
  const d = state.S[state.person];
  // Tag elke schuld met z'n originele index voor we filteren — anders
  // refereren onclick-handlers (aflossen/setTermijn/...) naar de verkeerde
  // index wanneer er afgesloten schulden tussen zitten.
  const open = (d.schulden || [])
    .map((s, origI) => ({ ...s, __origI: origI }))
    .filter(s => s.rest > 0);
  const externOpen = (d.externeSchulden || []).filter(s => (s.rest || 0) > 0);
  if (!open.length && !externOpen.length) return '';

  const rows = open.map((s) => {
    const i = s.__origI;
    const andereCap = nameFor(s.andere);
    const richtungLbl =
      s.kant === 'naar-samen'  ? 'Ik moet terugbetalen aan Samen' :
      s.kant === 'van-samen'   ? 'Samen moet mij terugbetalen' :
      s.kant === 'naar-spaar'  ? 'Ik moet terugstorten in spaarpot' :
      s.kant === 'van-andere'  ? `${andereCap} betaalt mij terug` :
      s.kant === 'naar-andere' ? `Ik moet terugbetalen aan ${andereCap}` :
      'Openstaand';
    const richtungIcon =
      s.kant === 'naar-samen'  ? '→' :
      s.kant === 'van-samen'   ? '←' :
      s.kant === 'naar-spaar'  ? '🐷' :
      s.kant === 'van-andere'  ? '←' :
      s.kant === 'naar-andere' ? '→' :
      '•';

    // Progress bar: hoeveel is al afbetaald?
    const pct = s.bedrag > 0 ? Math.min(100, (s.bedrag - s.rest) / s.bedrag * 100) : 0;

    // Wie ziet wat:
    //  - debtor (naar-*): kan termijn instellen, drukt Aflossen
    //  - creditor van-andere: ziet termijn als reminder, knop = Kwijtschelden
    //  - creditor van-samen: ziet geen termijn, knop = Aflossen (manueel settle)
    const isCreditorAndere = s.kant === 'van-andere';
    const isCreditorSamen  = s.kant === 'van-samen';
    const isDebtor = !isCreditorAndere && !isCreditorSamen;

    const editKey = 'schuld-' + (s.id || `i${i}`);
    const isEditing = !!state.editing[editKey];

    let termijnHTML;
    if (isCreditorSamen) {
      termijnHTML = '';
    } else if (isCreditorAndere) {
      // Creditor (Sven kant van-andere) ziet plan als read-only reminder.
      termijnHTML = `<div class="schuld-termijn">Plan: ${s.termijn ? `€${s.termijn}/mnd door ${andereCap}` : `${andereCap} heeft nog geen plan ingesteld`}</div>`;
    } else if (isEditing) {
      // Debtor in edit-mode: input zichtbaar.
      const termijnVal = s.termijn || '';
      termijnHTML = `<div class="schuld-termijn">
        Schijf:
        <input type="number" id="schuld-termijn-${i}" value="${termijnVal}" placeholder="0" step="1" min="0" style="width:60px">
        €/mnd
        <button class="schuld-termijn-save" onclick="setTermijn(${i})">Opslaan</button>
      </div>`;
    } else {
      // Debtor default: read-only weergave. Swipe-edit reveals input.
      termijnHTML = `<div class="schuld-termijn">Plan: ${s.termijn ? `€${s.termijn}/mnd per aflossen-klik` : 'geen plan — aflossen wist volledige rest'}</div>`;
    }

    const actionBtn = isCreditorAndere
      ? `<button class="schuld-pay schuld-kwijt" onclick="kwijtschelden(${i})">Kwijtschelden</button>`
      : `<button class="schuld-pay" onclick="aflossen(${i})">Aflossen</button>`;

    const rowHTML = `<div class="schuld-row">
      <div class="schuld-info">
        <div>${richtungIcon} ${s.desc || s.cat || 'Lening'}</div>
        <div class="schuld-meta">${richtungLbl} · ${s.date}</div>
        <div class="schuld-progress-bg">
          <div class="schuld-progress-fill" style="width:${pct}%"></div>
        </div>
        <div class="schuld-progress-meta">€${(s.bedrag - s.rest).toFixed(2)} / €${s.bedrag.toFixed(2)} afbetaald (${Math.round(pct)}%)</div>
        ${termijnHTML}
      </div>
      <div class="schuld-actions">
        <span class="schuld-amt">€${s.rest.toFixed(2)}</span>
        ${actionBtn}
      </div>
    </div>`;

    // Alleen debtor side krijgt swipe-edit; creditor & samen-overview niet.
    // Elke rij krijgt z'n eigen .schuld-item kader (UX-verzoek Lander).
    const inner = isDebtor
      ? wrapSwipe(rowHTML, { edit: `toggleEditSchuld(${i})` })
      : rowHTML;
    return `<div class="schuld-item">${inner}</div>`;
  }).join('');

  // Externe-schulden-sectie. Avalanche-hint enkel bij 2+ openstaande.
  let externSection = '';
  if (externOpen.length) {
    const av = avalancheVolgorde(state.person);
    const hint = av.length >= 2
      ? `<div class="extern-hint">💡 Begin bij hoogste rente: <strong>${av[0].n}</strong> (${av[0].rente}%) — bespaart meeste rentekosten.</div>`
      : '';
    const rowsExt = externOpen.map(s => {
      const pct = s.hoofdsom > 0
        ? Math.min(100, (s.hoofdsom - s.rest) / s.hoofdsom * 100)
        : 0;
      return `<div class="schuld-item"><div class="schuld-row">
        <div class="schuld-info">
          <div>💳 ${s.n}<span class="rente-badge">${(s.rente || 0)}%</span></div>
          <div class="schuld-meta">min €${(s.minMaand || 0).toFixed(2)}/mnd${s.startDate ? ` · sinds ${s.startDate}` : ''}</div>
          <div class="schuld-progress-bg">
            <div class="schuld-progress-fill" style="width:${pct}%"></div>
          </div>
          <div class="schuld-progress-meta">€${(s.hoofdsom - s.rest).toFixed(2)} / €${s.hoofdsom.toFixed(2)} afbetaald (${Math.round(pct)}%)</div>
        </div>
        <div class="schuld-actions">
          <span class="schuld-amt">€${s.rest.toFixed(2)}</span>
          <button class="schuld-pay" onclick="aflossenExterneSchuld(${s.id})">Aflossen</button>
        </div>
      </div></div>`;
    }).join('');
    externSection = `<div class="schuld-subhead">Externe schulden</div>${hint}${rowsExt}`;
  }

  const houshHead = open.length
    ? `<div class="schuld-subhead">Openstaand met Samen</div>${rows}`
    : '';

  return `<div class="schuld-section">
    <div class="card-title" style="margin-bottom:6px">⚖️ Openstaande leningen</div>
    ${houshHead}
    ${externSection}
  </div>`;
}

// -----------------------------------------------------------------
// Absence-modal — "Lang weg geweest" (≥ 90 dagen inactief)
// -----------------------------------------------------------------

/** Getriggerd vanuit hydrate.js. Vraagt of de gebruiker de app-startdatum
 *  wil resetten naar vandaag, zodat "Rest vorige maand" niet langer een
 *  stale baseline gebruikt. Twee keuzes: ja → resetStartDate;
 *  nee → dismissAbsence. Beide sluiten de modal. */
export function buildAbsenceModal() {
  if (!state.absencePopupOpen) return '';
  const m = state.absenceMonths || 0;

  return `<div class="modal-overlay" onclick="if(event.target===this) dismissAbsence()">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">Welkom terug! 👋</div>
        <button class="modal-close" onclick="dismissAbsence()">×</button>
      </div>
      <div style="padding:4px 0 12px 0;font-size:14px;line-height:1.45">
        Je hebt Florijn al <strong>${m} maanden</strong> niet meer geopend. Wil je je startdatum bijwerken naar vandaag, zodat "Rest vorige maand" enkel vanaf nu telt?
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button class="bcancel" onclick="dismissAbsence()">Nee, hou bij oude</button>
        <button class="badd"    onclick="resetStartDate()">Ja, frisse start</button>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Bespaar-modal — duurste items inline bewerkbaar
// -----------------------------------------------------------------

export function buildBespaarModal() {
  if (!state.modalBespaarOpen) return '';
  const bs = bespaarStatus(state.person);
  const items = expensiveItems(state.person, 10);

  const rows = items.map(it => {
    const cmeta = catById(it.cat);
    const fM = freqM(it);
    const monthly = ma(it);
    const id = `besp-${it.__type}-${it.__i}`;
    return `<div class="bespaar-row">
      <div class="bespaar-info">
        <span class="bespaar-emoji">${cmeta.emoji}</span>
        <div>
          <div class="bespaar-name">${it.n}</div>
          <div class="bespaar-meta">${cmeta.label} · ${fM === 1 ? 'maandelijks' : fM === 12 ? 'jaarlijks' : `om de ${fM} mnd`} · €${monthly.toFixed(2)}/mnd</div>
        </div>
      </div>
      <div class="bespaar-input">
        €<input type="number" id="${id}" value="${it.a.toFixed(2)}" step="0.01" data-type="${it.__type}" data-i="${it.__i}">
      </div>
    </div>`;
  }).join('') || '<div style="font-size:12px;color:var(--color-text-secondary);padding:8px 0">Geen items om te bewerken.</div>';

  return `<div class="modal-overlay" onclick="if(event.target===this) closeBespaar()">
    <div class="modal modal-wide">
      <div class="modal-head">
        <div class="modal-title">💰 Besparen</div>
        <button class="modal-close" onclick="closeBespaar()">×</button>
      </div>
      <p style="font-size:13px;color:var(--color-text-secondary);margin-bottom:.75rem">
        Pas hier prijzen aan om vaste/variabele kosten onder de 50/30 maxima te krijgen. Boodschappen en Gezamenlijke kosten zitten er bewust niet bij.
      </p>
      <div style="font-size:12px;color:var(--color-text-secondary);margin-bottom:.85rem;background:var(--color-background-secondary);padding:8px 10px;border-radius:6px">
        ${bs.overVast ? `🔴 Vaste kosten: <strong>€${bs.vastTotal.toFixed(0)}</strong> / max €${bs.targetVast.toFixed(0)} (+€${bs.teveelVast.toFixed(0)})<br>` : ''}
        ${bs.overVar  ? `🔴 Variabele kosten: <strong>€${bs.varTotal.toFixed(0)}</strong> / max €${bs.targetVar.toFixed(0)} (+€${bs.teveelVar.toFixed(0)})` : ''}
      </div>
      ${rows}
      <div class="modal-actions">
        <button onclick="closeBespaar()">Annuleer</button>
        <button class="bsave" onclick="saveBespaar()">Opslaan</button>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Reflect-modal — maandafsluit-ritueel (Fase E)
// -----------------------------------------------------------------

/** "Vergeet je bankapp"-toast — verschijnt na klik op "Vanaf nu automatisch"
 *  op de gem- of spaar-storting-melding. Florijn neemt vanaf nu aan dat de
 *  storting vanzelf gebeurt; de gebruiker moet dat zelf in z'n bankapp
 *  zetten als domiciliëring of geplande overschrijving. */
export function buildAutoNoticeModal() {
  if (!state.modalAutoNoticeOpen) return '';
  const kind = state.modalAutoNoticeKind;
  const isGem = kind === 'gem';
  const title = isGem ? 'Gezamenlijke storting automatisch' : 'Sparen automatisch';
  const what  = isGem
    ? 'maandelijks je <strong>aandeel</strong> in de gezamenlijke kosten naar de samen-rekening'
    : 'maandelijks je <strong>spaarbedrag</strong> naar de spaarpot';

  return `<div class="modal-overlay" onclick="if(event.target===this) closeAutoNotice()">
    <div class="modal-content auto-notice">
      <h2 class="auto-notice-title">🏦 Vergeet je bankapp niet!</h2>
      <p class="auto-notice-body">
        Vanaf nu ga ik er vanuit dat ${what} vanzelf gebeurt. Heb je die overschrijving al ingepland in je bankapp (als <strong>domiciliëring</strong> of <strong>geplande overschrijving</strong>)? Zo niet → doe 't best meteen, anders staan mijn cijfers straks niet meer in lijn met je rekening.
      </p>
      <div class="auto-notice-actions">
        <button class="auto-notice-btn" onclick="closeAutoNotice()">Begrepen 👍</button>
      </div>
    </div>
  </div>`;
}

/** "Deze maand niet sparen"-modal — financieel-coachende tussenstap
 *  voor wie de spaar-notif wil overslaan. Twee views:
 *    'tip'    → uitleg waarom 't toch slim is + "Sla over" of "Wel sparen"
 *    'custom' → eigen bedrag voor deze maand (verdeeld over dromen). */
export function buildSpaarSkipModal() {
  if (!state.modalSpaarSkipOpen) return '';
  const view = state.modalSpaarSkipView || 'tip';
  const fullAmt = spaarbedrag(state.person);

  if (view === 'tip') {
    return `<div class="modal-overlay" onclick="if(event.target===this) closeSpaarSkip()">
      <div class="modal-content spaar-skip-modal">
        <h2 class="spaar-skip-title">💭 Toch niet deze maand?</h2>
        <div class="spaar-skip-body">
          <p>Sparen is vooral een <strong>gewoonte</strong>, geen bedrag. Eén maand overslaan voelt klein, maar zet de teller op nul — volgende maand kost 't méér moeite om 't weer op te pakken.</p>
          <p style="margin-top:10px"><strong>💡 Tip:</strong> kies een lager bedrag in plaats van helemaal overslaan. Zelfs <strong>€20</strong> houdt je anker overeind.</p>
        </div>
        <div class="spaar-skip-actions">
          <button class="spaar-skip-btn-secondary" onclick="skipSpaar()">Toch overslaan</button>
          <button class="spaar-skip-btn-primary" onclick="gotoSpaarCustom()">💪 Iets sparen</button>
        </div>
      </div>
    </div>`;
  }

  // 'custom' view: eigen bedrag invoeren
  return `<div class="modal-overlay" onclick="if(event.target===this) closeSpaarSkip()">
    <div class="modal-content spaar-skip-modal">
      <h2 class="spaar-skip-title">💪 Hoeveel deze maand?</h2>
      <div class="spaar-skip-body">
        <p>Origineel: <strong>€${fullAmt.toFixed(2)}</strong> (20% van je vast loon). Kies een bedrag dat haalbaar voelt:</p>
        <div class="spaar-skip-input-row">
          <span class="spaar-skip-currency">€</span>
          <input id="spaar-skip-amt" type="number" inputmode="decimal" step="0.01" min="0.01"
                 placeholder="20" value="" autofocus class="spaar-skip-input">
        </div>
      </div>
      <div class="spaar-skip-actions">
        <button class="spaar-skip-btn-secondary" onclick="spaarSkipBack()">‹ Terug</button>
        <button class="spaar-skip-btn-primary" onclick="saveSpaarCustom()">Opslaan</button>
      </div>
    </div>
  </div>`;
}

/** Abonn-check-sectie binnen de reflectie-modal. Toont max 2 random
 *  abonn-items die >30 dagen niet bevestigd zijn (state lockt de ids
 *  vast bij openReflect zodat de selectie niet wisselt op render). */
function buildReflectAbonns() {
  const picks = abonnsForReflection(state.person, state.reflectionAbonnIds);
  if (!picks.length) return '';

  const rows = picks.map(({ it, idx, days }) => {
    const dgs = days === Infinity ? 'nooit bevestigd' : `${days} dagen geleden`;
    return `<div class="reflect-abonn-row">
      <div class="reflect-abonn-info">
        <div class="reflect-abonn-name">🎟️ ${it.n} <span class="reflect-abonn-amt">€${it.a.toFixed(2)}</span></div>
        <div class="reflect-abonn-meta">Laatst bevestigd: ${dgs}</div>
      </div>
      <div class="reflect-abonn-btns">
        <button class="reflect-abonn-btn reflect-abonn-btn-yes" onclick="abonnConfirm(${idx})" title="Ja, gebruikt">✓ Ja</button>
        <button class="reflect-abonn-btn reflect-abonn-btn-no" onclick="abonnRemove(${idx})" title="Verwijder abonnement">✗ Stop</button>
        <button class="reflect-abonn-btn reflect-abonn-btn-skip" onclick="abonnSkip(${idx})" title="Sla over voor deze cyclus">→ Later</button>
      </div>
    </div>`;
  }).join('');

  return `
    <div style="margin-top:14px;padding-top:14px;border-top:1px solid var(--color-border-tertiary)">
      <div style="font-size:12px;color:var(--color-text-secondary);margin-bottom:8px">Gebruik je deze nog?</div>
      <div class="reflect-abonn-list">${rows}</div>
    </div>`;
}

export function buildReflectModal() {
  if (!state.modalReflectOpen) return '';
  const cKey = state.modalReflectCycle;
  if (!cKey) return '';
  const seg = homeSegs(state.person, cKey);
  const [y, m] = cKey.split('-').map(Number);
  // cycleKey 'YYYY-MM' heeft MM als 0-11 (dateToCycleKey).
  const lbl = MONTHS[Math.max(0, Math.min(11, m))] + ' ' + y;
  const review = state.S[state.person]?.cycleReviews?.[cKey] || {};
  const topCats = seg.cats.slice(0, 3);

  // Cats die in deze cyclus boven hun budget gingen — recap voor de
  // reflectie. Negeert de "Oké"-acks bewust, want het maandoverzicht
  // hoort de volledige stand te tonen ook al heeft de gebruiker dagelijks
  // gedismissed.
  const overWeek    = catsOverMaandbudget(state.person, cKey);
  const overPlanned = catsOverPlanned(state.person, cKey);
  const overItems = [
    ...overWeek.map(o => ({
      catId: o.catId, spent: o.spent, doel: o.budget,
      over: o.spent - o.budget, soort: 'week'
    })),
    ...overPlanned.map(o => ({
      catId: o.catId, spent: o.spent, doel: o.planned,
      over: o.over, soort: 'plan'
    }))
  ];
  const overBlock = overItems.length ? `
    <div style="font-size:12px;color:var(--color-text-secondary);margin-bottom:4px">Boven budget gegaan</div>
    <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:12px">
      ${overItems.map(o => {
        const cm = catById(o.catId);
        const lbl = o.soort === 'week' ? 'maandbudget' : 'plan';
        return `<div style="display:flex;justify-content:space-between;font-size:13px;padding:4px 8px;background:#fdecea;border:.5px solid #e9aaa4;border-radius:6px">
          <span>${cm.emoji} ${cm.label} <span style="color:var(--color-text-secondary);font-size:11px">· ${lbl}</span></span>
          <strong style="color:#c0392b">+€${o.over.toFixed(0)}</strong>
        </div>`;
      }).join('')}
    </div>` : '';

  return `<div class="modal-overlay" onclick="if(event.target===this) closeReflect()">
    <div class="modal modal-wide">
      <div class="modal-head">
        <div class="modal-title">📓 Reflectie — ${lbl}</div>
        <button class="modal-close" onclick="closeReflect()">×</button>
      </div>
      <div style="padding:4px 0 12px 0">
        <div style="font-size:12px;color:var(--color-text-secondary);margin-bottom:6px">Cijfers van die cyclus</div>
        <div class="kpi-mini-row" style="margin-bottom:8px">
          <div class="kpi-mini">
            <div class="kpi-mini-lbl">Inkomen</div>
            <div class="kpi-mini-amt">€${seg.inkomst.toFixed(0)}</div>
          </div>
          <div class="kpi-mini">
            <div class="kpi-mini-lbl">Uitgegeven</div>
            <div class="kpi-mini-amt">€${seg.uitgegeven.toFixed(0)}</div>
          </div>
          <div class="kpi-mini">
            <div class="kpi-mini-lbl">${seg.resterend >= 0 ? 'Overgehouden' : 'Tekort'}</div>
            <div class="kpi-mini-amt" style="color:${seg.resterend >= 0 ? 'var(--color-text-primary)' : '#c0392b'}">€${Math.abs(seg.resterend).toFixed(0)}</div>
          </div>
        </div>
        ${topCats.length ? `<div style="font-size:12px;color:var(--color-text-secondary);margin-bottom:4px">Top 3 categorieën</div>
        <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:12px">
          ${topCats.map(c => `<div style="display:flex;justify-content:space-between;font-size:13px;padding:4px 8px;background:var(--color-background-secondary);border-radius:6px">
            <span>${c.emoji} ${c.label}</span>
            <strong>€${c.amount.toFixed(0)}</strong>
          </div>`).join('')}
        </div>` : ''}
        ${overBlock}
        ${buildReflectAbonns()}
        <div class="field" style="margin-top:8px">
          <label>Wat zou je anders willen volgende cyclus? <span style="color:var(--color-text-secondary);font-weight:400">(optioneel)</span></label>
          <textarea id="reflect-note" rows="3" style="width:100%;font-size:13px;padding:8px;border:1px solid var(--color-border-tertiary);border-radius:6px;resize:vertical">${review.note || ''}</textarea>
        </div>
        <div style="display:flex;gap:8px;margin-top:12px;justify-content:flex-end">
          <button class="bcancel" onclick="closeReflect()">Annuleer</button>
          <button class="badd"    onclick="saveReflect()">Klaar — reflectie opgeslagen</button>
        </div>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Settings-modal — app-instellingen + categorieen CRUD
// -----------------------------------------------------------------

export function buildSettingsModal() {
  if (!state.modalSettingsOpen) return '';

  const view = state.settingsView || 'menu';
  const screen = settingsScreen(view);
  const back = view === 'menu'
    ? '<span class="modal-back-spacer"></span>'
    : `<button class="modal-back" onclick="settingsBack()" title="Terug">‹</button>`;

  return `<div class="modal-overlay" onclick="if(event.target===this) closeSettings()">
    <div class="modal modal-wide modal-settings">
      <div class="modal-head modal-head-nav">
        ${back}
        <div class="modal-title">${screen.title}</div>
        <button class="modal-close" onclick="closeSettings()">×</button>
      </div>
      ${screen.body}
    </div>
  </div>`;
}

/** Dispatcher: kies titel + body voor de gevraagde sub-view. */
function settingsScreen(view) {
  switch (view) {
    case 'user':
    case 'identiteit':
    case 'app':         return { title: '👤 User',                      body: viewUser() };
    case 'household':   return { title: '🏠 Huishouden',                body: viewHousehold() };
    case 'cashflow':    return { title: '💰 Cashflow',                  body: viewCashflow() };
    case 'weekbudgets': return { title: '🍱 Weekbudgetten',              body: viewWeekBudgets() };
    case 'belasting':   return { title: '🧾 Belastingvoordeel (BE)',    body: viewBelasting() };
    case 'cats-uit':    return { title: '📤 Categorieën — Uitgaven',    body: viewCatsList('uit') };
    case 'cats-in':     return { title: '📥 Categorieën — Inkomsten',   body: viewCatsList('in') };
    case 'cat':         return { title: viewCatTitle(),                 body: viewCat() };
    case 'extschulden': return { title: '💳 Externe schulden',          body: externeSchuldenSection() };
    case 'backup':      return { title: '💾 Backup & export',             body: viewBackupExport() };
    case 'handleiding': return { title: '❓ Handleiding',                  body: viewHandleiding() };
    case 'gevarenzone': return { title: '⚠️ Gevarenzone',                body: viewGevarenzone() };
    case 'menu':
    default:            return { title: '⚙️ Instellingen',               body: viewSettingsMenu() };
  }
}

/** Hoofdmenu — lijst van nav-rijen, één per sectie. */
function viewSettingsMenu() {
  const t = state.S.settings?.theme || 'auto';
  const sd = state.S.settings?.appStartDate;
  const themeLbl = { auto: 'Auto', default: 'Standaard', pride: 'Pride' }[t] || 'Auto';
  const showPersonalSections = state.person !== 'samen';
  const pp = state.S[state.person] || {};
  const huidig = pp.huidigSaldo;
  const pActief = !!pp.pensioenActief;
  const catCountUit = (state.S.cats || []).filter(c => c.scope === 'uit').length;
  const catCountIn  = (state.S.cats || []).filter(c => c.scope === 'in').length;
  const extCount    = (pp.externeSchulden || []).length;

  const row = (action, emoji, lbl, sub, extra = '') => `
    <button class="set-row${extra}" onclick="settingsGoTo('${action}')">
      <span class="set-row-emoji">${emoji}</span>
      <span class="set-row-lbl">${lbl}</span>
      ${sub ? `<span class="set-row-sub">${sub}</span>` : ''}
      <span class="set-row-arrow">›</span>
    </button>`;

  // Huishouden-status voor sub-tekst van de menu-rij. Altijd zichtbaar
  // (geen conditional meer); status verschilt naar geen / wel huishouden
  // en — als er wel een huishouden is — of de medebewoner al aangesloten is.
  const hhSub = !state.householdId
    ? 'aanmaken'
    : (state.mateConnected ? 'medebewoner aangesloten' : 'medebewoner uitnodigen');

  return `<div class="set-menu">
    ${row('user', '👤', 'User', `${nameFor(state.user)} · ${themeLbl}${sd ? ' · start ' + sd : ''}`)}
    ${row('household', '🏠', 'Huishouden', hhSub)}
    ${showPersonalSections ? row('cashflow', '💰', 'Cashflow', huidig != null ? `€${huidig.toFixed(0)}` : 'niet ingesteld') : ''}
    ${showPersonalSections ? row('weekbudgets', '🍱', 'Weekbudgetten', weekBudgetsSummary(pp)) : ''}
    ${showPersonalSections ? row('belasting', '🧾', 'Belastingvoordeel', pActief ? 'pensioensparen aan' : 'uit') : ''}
    ${row('cats-uit', '📤', 'Categorieën — Uitgaven', `${catCountUit} items`)}
    ${row('cats-in', '📥', 'Categorieën — Inkomsten', `${catCountIn} items`)}
    ${showPersonalSections ? row('extschulden', '💳', 'Externe schulden', `${extCount} item${extCount === 1 ? '' : 's'}`) : ''}
    ${row('backup', '💾', 'Backup & export', 'JSON downloaden')}
    ${row('handleiding', '❓', 'Handleiding', 'hoe Florijn werkt')}
    ${row('gevarenzone', '⚠️', 'Gevarenzone', '', ' set-row-danger')}
  </div>`;
}

function viewUser() {
  // Account-blok: identiteit wordt sinds de auth-switch bepaald door wie er
  // is ingelogd — geen toggle meer. Read-only zodat 't kader duidelijk maakt
  // wie z'n data het over gaat voor de sharing-toggle eronder.
  const me   = nameFor(state.user);
  const mate = nameFor(state.user === 'sven' ? 'lander' : 'sven');

  // App-blok: startdatum + mode + theme. Mode + theme apart — backwards-compat:
  // oude 'theme: dark/light' valt automatisch terug op default + juiste mode.
  const startDate = state.S.settings?.appStartDate || '';
  const mode  = state.S.settings?.mode  || (state.S.settings?.theme === 'dark' ? 'dark' : 'light');
  const fabSide = readFabSide();
  const rawT  = state.S.settings?.theme || 'default';
  const savedTheme  = ['default', 'frietjes', 'lowie', 'pride'].includes(rawT) ? rawT : 'default';
  // Wat is er momenteel echt actief? Tijdens auto-pride is dat 'pride' ook
  // als savedTheme nog 'default' is — de picker highlight de actieve tile.
  const activeTheme = getActiveTheme();
  const autoPride   = isAutoPrideActive();

  return `
    <div class="field">
      <label>Account</label>
      <div class="set-readonly">${me}</div>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
        Vastgelegd door je login. Wil je een andere weergavenaam? Log uit en maak een nieuw account met die username.
      </small>
    </div>
    ${state.mateConnected ? `
      <div class="field" style="margin-top:.85rem">
        <label class="cb-row cb-block">
          <input type="checkbox" ${state.S[state.user]?.shareWithMate ? 'checked' : ''}
            onchange="setShareWithMate(this.checked)">
          <span>👁 Laat ${mate} mijn data inkijken (read-only)</span>
        </label>
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
          Schakelt een kijker-knop in bovenaan. Bewerken blijft enkel voor de eigenaar.
        </small>
      </div>` : `
      <div class="field" style="margin-top:.85rem">
        <small style="color:var(--color-text-secondary);font-size:12px;display:block;padding:10px 12px;background:var(--color-background-secondary);border-radius:var(--border-radius-md);line-height:1.45">
          🔗 Zodra je medebewoner aansluit, verschijnt hier een instelling om hem/haar in jouw data te laten kijken. Beheer je uitnodiging via <strong>Instellingen → 🏠 Huishouden</strong>.
        </small>
      </div>`}
    <div class="field" style="margin-top:1.25rem">
      <label>Automatische stortingen</label>
      ${state.hasSharedAccount ? `
        <label class="cb-row cb-block" style="margin-top:4px">
          <input type="checkbox" ${state.S[state.user]?.gemAuto ? 'checked' : ''}
            onchange="toggleGemAuto(this.checked)">
          <span>💶 Gem-storting verloopt automatisch</span>
        </label>` : ''}
      <label class="cb-row cb-block" style="margin-top:4px">
        <input type="checkbox" ${state.S[state.user]?.spaarAuto ? 'checked' : ''}
          onchange="toggleSpaarAuto(this.checked)">
        <span>🐷 Sparen verloopt automatisch</span>
      </label>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:6px;line-height:1.45">
        Wanneer aan: Florijn vraagt 't niet meer maandelijks, en gaat er vanuit dat de storting via je bankapp gebeurt. Uit-zetten → de melding komt terug bij de volgende cyclus.
      </small>
    </div>
    <div class="field" style="margin-top:1.25rem">
      <label>App startdatum</label>
      <input type="date" id="set-startdate" value="${startDate}">
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
        Cycli die vóór deze datum eindigden tellen niet mee voor "Rest vorige maand".
      </small>
      <button class="badd" style="margin-top:6px" onclick="saveAppStart()">Opslaan</button>
    </div>
    <div class="field" style="margin-top:1.25rem">
      <label>"Nog over"-weergave op Home</label>
      <div class="seg">
        <button class="${(state.S.settings?.remainingView || 'month') === 'month' ? 'active' : ''}" onclick="saveRemainingView('month')">📅 Maand</button>
        <button class="${state.S.settings?.remainingView === 'week' ? 'active' : ''}" onclick="saveRemainingView('week')">📆 Week</button>
      </div>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px;line-height:1.45">
        <strong>Maand</strong>: klassiek — inkomen − alle uitgaven in de cyclus.<br>
        <strong>Week</strong>: maandelijks resterend ÷ aantal weken in de cyclus, − je off-plan uitgaven van deze week (Ma–Zo). Eerste week start op je loon-anker-dag, laatste eindigt op de laatste cyclus-dag.
      </small>
    </div>
    <div class="field" style="margin-top:1.25rem">
      <label>Modus</label>
      <div class="seg">
        <button class="${mode === 'light' ? 'active' : ''}" onclick="saveMode('light')">☀️ Light</button>
        <button class="${mode === 'dark'  ? 'active' : ''}" onclick="saveMode('dark')">🌙 Dark</button>
      </div>
    </div>
    <div class="field" style="margin-top:1.25rem">
      <label>+ knop links of rechts (per toestel)</label>
      <div class="seg">
        <button class="${fabSide === 'right' ? 'active' : ''}" onclick="setFabSide('right')">👉 Rechts</button>
        <button class="${fabSide === 'left'  ? 'active' : ''}" onclick="setFabSide('left')">👈 Links</button>
      </div>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
        Voor linkshandig gebruik. Deze keuze blijft per toestel — gsm en pc hoeven niet hetzelfde te zijn.
      </small>
    </div>
    <div class="field" style="margin-top:1.25rem">
      <label>Kleur-thema
        <button class="info-btn" style="width:14px;height:14px;font-size:8px;margin-left:4px" title="Over het Pride-thema" onclick="toggleForm('info-pride-auto')">i</button>
      </label>
      ${buildThemePicker(savedTheme, activeTheme, mode, autoPride)}
      <div class="info-block" id="info-pride-auto" style="display:none;margin-top:8px">
        <strong>Pride-thema 🏳️‍🌈</strong> — op <strong>17 mei (IDAHOT)</strong> en de hele maand <strong>juni</strong> hijst Florijn automatisch de regenboogvlag: sta je op "Groen", dan switcht hij vanzelf naar Pride. Erna keurig terug naar je standaard.
        <br><br>
        Want zichtbaarheid telt — ook wanneer je jouw budget beheert in Florijn! 🌈
        <br><br>
        <em>Wist je dat?</em> IDAHOT (17 mei) bestaat omdat de WHO op die dag in 1990 homoseksualiteit van de lijst van ziekten schrapte. Iets om elk jaar even bij stil te staan.
        <br><br>
        <em>Status:</em> auto-schakel staat aan. Wie expliciet voor Frietjes of Lowie koos blijft daar — alleen het standaard-thema switcht vanzelf mee tijdens het Pride-seizoen.
      </div>
    </div>
    <div class="field" style="margin-top:1.25rem;padding-top:1rem;border-top:1px solid var(--color-border-tertiary)">
      <label>App-versie</label>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px;line-height:1.45">
        Florijn checkt zelf elk uur of er een nieuwe versie klaar staat. Verschijnt er een melding rechtsonder, tik daarop om bij te werken — zo hoef je de app op je startscherm nooit meer weg te halen om bij te zijn.
      </small>
      <button class="badd" style="margin-top:6px" onclick="checkAppUpdate(this)">↻ Check op updates</button>
      <span id="pwa-check-result" style="display:block;margin-top:6px;font-size:11px;color:var(--color-text-secondary)"></span>
    </div>`;
}

/** Korte sub-tekst voor de Weekbudgetten-rij in het settings-menu —
 *  toont hoeveel cats er een budget hebben en de totaal-som per week. */
function weekBudgetsSummary(pp) {
  const wb = pp?.weekBudgets || {};
  const entries = Object.entries(wb).filter(([, v]) => Number(v) > 0);
  if (entries.length === 0) return 'niet ingesteld';
  const total = entries.reduce((s, [, v]) => s + Number(v), 0);
  return `${entries.length} cat · €${total.toFixed(0)}/week`;
}

/** Weekbudgetten-sectie — toont alle "uit"-cats (zonder locked) met een
 *  inline input voor hun weekbudget. Save fire's gewone saveWE (al gewired
 *  voor Supabase-sync). Leeg of 0 → bewaart 0 (Home-widget verbergt 'm). */
function viewWeekBudgets() {
  const cats = (state.S.cats || [])
    .filter(c => c.scope === 'uit' && !c.locked);
  const wb = state.S[state.person]?.weekBudgets || {};
  const total = Object.values(wb)
    .reduce((s, v) => s + (Number(v) > 0 ? Number(v) : 0), 0);

  const rows = cats.map(c => {
    const val = Number(wb[c.id]) > 0 ? Number(wb[c.id]) : '';
    return `
      <div class="wb-row">
        <div class="wb-cat">
          <span class="wb-emoji">${c.emoji}</span>
          <span class="wb-lbl">${c.label}</span>
        </div>
        <div class="wb-edit">
          <span class="wb-currency">€</span>
          <input id="wb-${c.id}" type="number" inputmode="decimal" step="0.01" min="0"
                 placeholder="0" value="${val}" class="wb-input">
          <span class="wb-suffix">/week</span>
          <button class="bsave wb-save" onclick="saveWE('${c.id}')">Opslaan</button>
        </div>
      </div>`;
  }).join('');

  return `
    <div class="field">
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;line-height:1.45;margin-bottom:10px">
        Stel een weekbudget in per categorie. Cats met een budget krijgen automatisch een week-widget op Home. Leeg of 0 → geen budget. Wijzigingen worden direct opgeslagen wanneer je op <strong>Opslaan</strong> klikt.
      </small>
      <div class="wb-list">
        ${rows || '<small style="color:var(--color-text-secondary)">Geen categorieën gevonden. Voeg er één toe in Categorieën — Uitgaven.</small>'}
      </div>
      ${total > 0 ? `<div class="wb-total"><strong>Totaal:</strong> €${total.toFixed(2)}/week (≈ €${(total * 4).toFixed(0)}/maand)</div>` : ''}
    </div>`;
}

/**
 * Huishouden-sectie — twee modes:
 *   - Zonder huishouden → naam-input + "Huishouden aanmaken"-knop
 *     (roept setupNewHousehold RPC aan, zelfde als setup-gate)
 *   - Met huishouden → naam-edit + invite-link + kopieer-knop. Bovenaan
 *     status-line die meldt of de medebewoner al aangesloten is.
 *
 * Invite-link wordt afgeleid van state.householdId — geen aparte storage,
 * household.id IS de uitnodigingscode (vergelijk URL ?invite=<uuid>).
 */
function viewHousehold() {
  // Zonder huishouden: aanmaak-flow.
  if (!state.householdId) {
    const me = nameFor(state.user);
    return `
      <div class="field">
        <label>Naam van het huishouden</label>
        <input id="set-hh-name" type="text"
               placeholder="${me} &amp; co" autocomplete="off" required>
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
          Begin een nieuw Florijn-budget voor jullie samen. Daarna kan je een medebewoner uitnodigen via een link.
        </small>
        <button class="badd" style="margin-top:10px" onclick="createHouseholdFromSettings()">🏠 Huishouden aanmaken</button>
      </div>`;
  }

  // Met huishouden: status, naam-edit, invite-link.
  const hhName = state.householdName || '';     // gevuld na fetchHousehold (zie actions)
  const inviteLink = `${window.location.origin}/?invite=${state.householdId}`;
  const statusLine = state.mateConnected
    ? `<div class="set-readonly" style="border-color:#2a9d5a;color:#2a9d5a">✓ Medebewoner aangesloten</div>`
    : `<div class="set-readonly" style="border-color:#e67e22;color:#e67e22">🔗 Nog geen medebewoner aangesloten</div>`;

  return `
    <div class="field">
      <label>Status</label>
      ${statusLine}
    </div>
    <div class="field" style="margin-top:.85rem">
      <label>Naam van het huishouden</label>
      <input id="set-hh-name" type="text" value="${escapeHtmlAttr(hhName)}"
             placeholder="${escapeHtmlAttr(nameFor(state.user) + ' & ' + nameFor(state.user === 'sven' ? 'lander' : 'sven'))}" autocomplete="off">
      <button class="badd" style="margin-top:6px" onclick="saveHouseholdName()">Opslaan</button>
    </div>
    <div class="field" style="margin-top:.85rem">
      <label class="cb-row cb-block">
        <input type="checkbox" ${state.hasSharedAccount ? 'checked' : ''}
          onchange="toggleSharedAccount(this.checked)">
        <span>💳 We hebben een gezamenlijke rekening</span>
      </label>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px;line-height:1.45">
        Pas wanneer dit aanstaat verschijnt de <strong>Samen</strong>-weergave in de account-dropdown en kan je gezamenlijke kosten boeken. Uit-zetten verbergt 't gewoon — data blijft bewaard.
      </small>
    </div>
    ${!state.mateConnected ? `
      <div class="field" style="margin-top:1.25rem">
        <label>Uitnodigingslink</label>
        <input id="set-invite-link" type="text" value="${escapeHtmlAttr(inviteLink)}" readonly
               style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;background:var(--color-background-secondary)">
        <button class="badd" style="margin-top:6px" onclick="copyInviteFromSettings(this)">Kopieer link</button>
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:6px;line-height:1.45">
          Stuur deze link naar je medebewoner — daarmee kan hij of zij aansluiten bij jouw huishouden. Zodra dat gebeurt verdwijnt deze sectie en kan je de share-checkbox in <strong>👤 User</strong> aanzetten.
        </small>
      </div>` : ''}
    <div class="field" style="margin-top:1.25rem">
      <small style="color:#c0392b;font-size:11px;display:block;line-height:1.45">
        ⚠️ <strong>Huishouden verlaten</strong> ontkoppelt je profile van dit huishouden. Je samen-data verdwijnt uit jouw zicht (blijft wel bestaan voor je medebewoner als die aangesloten is).
      </small>
      <button class="bdel" style="margin-top:6px" onclick="leaveHousehold()">Huishouden verlaten</button>
    </div>`;
}

/** Eenvoudige HTML-attribute-escape voor user-controlled strings. */
function escapeHtmlAttr(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

/** Grid van thema-knoppen met kleur-swatch. Swatch toont de light-variant
 *  als de huidige modus light is, en de dark-variant als modus dark — zo
 *  zie je effectief wat je krijgt. */
/**
 * Theme-picker. Highlights de ACTIEF zichtbare theme — tijdens auto-Pride
 * licht de Pride-tile dus op, ook als saved === 'default'. Pride-tile krijgt
 * een klein "Auto"-badge zodat duidelijk is waarom hij actief is. Een
 * "Pride staat aan in juni"-hint onder de grid geeft tap-naar-uit-instructie.
 */
function buildThemePicker(savedTheme, activeTheme, mode, autoPride) {
  const dark = mode === 'dark';
  const themes = [
    {
      id: 'default',
      label: 'Groen',
      swatch: dark
        ? 'linear-gradient(135deg, #1a1a1a 50%, #2a9d5a 50%)'
        : 'linear-gradient(135deg, #ffffff 50%, #2a9d5a 50%)'
    },
    {
      id: 'frietjes',
      label: 'Frietjes',
      swatch: dark
        ? 'linear-gradient(135deg, #2A2520 50%, #C9A37C 50%)'
        : 'linear-gradient(135deg, #EAE9E2 50%, #997D61 50%)'
    },
    {
      id: 'lowie',
      label: 'Lowie',
      swatch: dark
        ? 'linear-gradient(135deg, #2D1F17 50%, #D9965B 50%)'
        : 'linear-gradient(135deg, #F5F2E7 50%, #BF6F41 50%)'
    },
    {
      id: 'pride',
      label: 'Pride 🏳️‍🌈',
      swatch: 'var(--pride-gradient)'
    }
  ];
  const autoHint = autoPride
    ? `<p class="theme-auto-hint">🏳️‍🌈 Pride is automatisch actief tot eind juni. Tap <strong>Groen</strong> om over te slaan dit jaar.</p>`
    : '';
  return `<div class="theme-grid">
    ${themes.map(t => {
      const isActive = activeTheme === t.id;
      const isAutoPrideTile = t.id === 'pride' && autoPride && savedTheme !== 'pride';
      const badge = isAutoPrideTile ? '<span class="theme-auto-badge">Auto</span>' : '';
      return `
        <button class="theme-btn${isActive ? ' active' : ''}" onclick="saveTheme('${t.id}')">
          <span class="theme-swatch" style="background:${t.swatch}"></span>
          <span class="theme-lbl">${t.label}</span>
          ${badge}
        </button>`;
    }).join('')}
  </div>${autoHint}`;
}

function viewCashflow() {
  if (state.person === 'samen') return '<p>Niet beschikbaar op Samen.</p>';
  const vSaldo = voorgesteldSaldo(state.person);
  const huidig = state.S[state.person]?.huidigSaldo;
  const upd    = state.S[state.person]?.huidigSaldoUpdated || 'nog niet ingesteld';
  return `
    <div class="field">
      <label>Huidig rekeningsaldo (€) <button class="info-btn" title="Hoe vul je dit best in?" onclick="toggleForm('info-saldo-tips')">i</button></label>
      <input type="number" id="set-saldo" step="0.01" value="${huidig ?? ''}" placeholder="bv. 1250.00">
      ${vSaldo !== null ? `<div style="margin-top:6px;font-size:12px;color:var(--color-text-secondary);display:flex;align-items:center;gap:8px;flex-wrap:wrap">
        <span>💡 Voorstel: <strong style="color:var(--color-text-primary)">€${vSaldo}</strong></span>
        <button class="bcancel" style="padding:3px 10px;font-size:11px" onclick="document.getElementById('set-saldo').value=${vSaldo}">Gebruik voorstel</button>
      </div>` : ''}
      <div class="info-block" id="info-saldo-tips" style="display:none;margin-top:10px">
        <strong>Hoe vul je dit best in?</strong><br>
        De meest accurate bron is je <strong>bankapp</strong> — kijk naar het saldo van je hoofdrekening op dit moment.<br><br>
        <strong>Wél meetellen:</strong> loon dat al binnenkwam, uitgaven die al van de rekening werden afgeschreven (ook gezamenlijke kosten die je doorstortte).<br>
        <strong>Niet meetellen:</strong> spaargeld op aparte rekeningen, beleggingen, contant geld in je portefeuille. Bedragen die nog moeten geboekt worden horen er niet in.
      </div>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:8px">
        Bijgewerkt: ${upd}.
      </small>
      <button class="badd" style="margin-top:6px" onclick="saveHuidigSaldo()">Opslaan</button>
    </div>`;
}

function viewBelasting() {
  if (state.person === 'samen') return '<p>Niet beschikbaar op Samen.</p>';
  const pp = state.S[state.person];
  const grens   = pp?.pensioenGrens ?? 1050;
  const bpct    = pp?.belastingPct  ?? 25;
  const pActief = !!pp?.pensioenActief;
  return `
    <div class="field">
      <label class="cb-row cb-block">
        <input type="checkbox" id="set-pensioen-actief" ${pActief ? 'checked' : ''} onchange="togglePensioen(this.checked)">
        <span>Pensioensparen volgen</span>
        <button class="info-btn" title="Wat is dit?" onclick="toggleForm('info-pensioen-actief')">i</button>
      </label>
      <div class="info-block" id="info-pensioen-actief" style="display:none;margin-top:8px">
        Zet aan als je in België aan pensioensparen doet (of wil starten). De app toont dan een tegel in Dromen met je jaarstatus en het fiscaal voordeel.
      </div>
    </div>
    ${pActief ? `<div class="field" style="margin-top:1.25rem">
      <label>Fiscale grens pensioensparen <button class="info-btn" title="Wat is dit?" onclick="toggleForm('info-pensioen-grens')">i</button></label>
      <select id="set-pensioen-grens">
        <option value="1050" ${grens === 1050 ? 'selected' : ''}>€1.050 — 30% belastingvoordeel (standaard)</option>
        <option value="1350" ${grens === 1350 ? 'selected' : ''}>€1.350 — 25% belastingvoordeel (verhoogd plafond)</option>
      </select>
      <div class="info-block" id="info-pensioen-grens" style="display:none;margin-top:8px">
        Tot €1.050/jaar krijg je 30% belastingvermindering; je kan ook kiezen voor het verhoogd plafond €1.350 met 25% voordeel.
      </div>
      <button class="badd" style="margin-top:6px" onclick="savePensioenGrens()">Opslaan</button>
    </div>` : ''}
    <div class="field" style="margin-top:1.25rem">
      <label>Reservering bij extra inkomst (%) <button class="info-btn" title="Wat is dit?" onclick="toggleForm('info-bpct')">i</button></label>
      <input type="number" id="set-belasting-pct" min="0" max="100" step="1" value="${bpct}">
      <div class="info-block" id="info-bpct" style="display:none;margin-top:8px">
        Bij belastbare extra inkomsten krijg je bij het toevoegen een vinkje "Reserveer X% voor belasting". Die euro's worden meteen als uitgave geboekt in "Belastingen".
      </div>
      <button class="badd" style="margin-top:6px" onclick="saveBelastingPct()">Opslaan</button>
    </div>`;
}

/** Cat-lijst (uit of in) — elke rij navigeert naar een focused edit-view. */
function viewCatsList(scope) {
  const usage = countCatUsage();
  const showWb = state.person !== 'samen';
  const cats = (state.S.cats || []).filter(c => c.scope === scope);

  const rows = cats.map(c => {
    const inUse = usage[c.id] || 0;
    const wbVal = showWb && scope === 'uit' ? weekBudgetFor(state.person, c.id) : 0;
    const sub = [
      c.locked ? '🔒' : null,
      wbVal > 0 ? `€${wbVal}/wk` : null,
      inUse > 0 ? `${inUse}× in gebruik` : null
    ].filter(Boolean).join(' · ');
    return `<button class="set-row set-row-cat" style="border-left:3px solid ${c.color}" onclick="editCat('${c.id}')">
      <span class="set-row-emoji">${c.emoji}</span>
      <span class="set-row-lbl">${c.label}</span>
      ${sub ? `<span class="set-row-sub">${sub}</span>` : ''}
      <span class="set-row-arrow">›</span>
    </button>`;
  }).join('');

  return `<div class="set-menu">${rows}</div>
    <button class="badd" style="margin-top:1rem;width:100%" onclick="openAddCat('${scope}')">
      + Nieuwe ${scope === 'uit' ? 'uitgaven' : 'inkomsten'}-categorie
    </button>`;
}

/** Eén cat editen of een nieuwe toevoegen — focused form. */
function viewCat() {
  const ec = state.editingCat;
  if (!ec) return '<p>Geen categorie geselecteerd.</p>';

  // Nieuwe categorie
  if (ec === '__new_uit' || ec === '__new_in') {
    const scope = ec === '__new_uit' ? 'uit' : 'in';
    const showWb = scope === 'uit' && state.person !== 'samen';
    return `
      <div class="field"><label>Emoji</label>
        <input type="text" id="nc-emoji" value="📦" maxlength="3" style="width:80px;text-align:center;font-size:22px">
      </div>
      <div class="field"><label>Naam</label>
        <input type="text" id="nc-label" placeholder="bv. Sport" autofocus>
      </div>
      <div class="field"><label>Kleur</label>
        <input type="color" id="nc-color" value="#7f8c8d" style="width:60px;height:36px;border:none;cursor:pointer;padding:0;border-radius:6px">
      </div>
      ${showWb ? `<div class="field">
        <label>Weekbudget voor ${nameFor(state.person)} (€) — optioneel</label>
        <input type="number" id="nc-wb" value="0" step="1" min="0">
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
          Cats met een weekbudget verschijnen als widget op Home + krijgen een overschrijdings-notif.
        </small>
      </div>` : ''}
      <div class="modal-actions">
        <button onclick="cancelEditCat()">Annuleer</button>
        <button class="bsave" onclick="addCat('${scope}')">Toevoegen</button>
      </div>`;
  }

  // Bestaande cat bewerken
  const c = (state.S.cats || []).find(x => x.id === ec);
  if (!c) return '<p>Categorie niet gevonden.</p>';
  const showWb = state.person !== 'samen' && c.scope === 'uit';
  const wbVal = showWb ? weekBudgetFor(state.person, c.id) : 0;

  return `
    <div class="field"><label>Emoji</label>
      <input type="text" id="ec-emoji-${c.id}" value="${c.emoji}" maxlength="3" style="width:80px;text-align:center;font-size:22px">
    </div>
    <div class="field"><label>Naam</label>
      <input type="text" id="ec-label-${c.id}" value="${c.label}">
    </div>
    <div class="field"><label>Kleur</label>
      <input type="color" id="ec-color-${c.id}" value="${c.color}" style="width:60px;height:36px;border:none;cursor:pointer;padding:0;border-radius:6px">
    </div>
    ${showWb ? `<div class="field">
      <label>Weekbudget voor ${nameFor(state.person)} (€)</label>
      <input type="number" id="ec-wb-${c.id}" value="${wbVal}" step="1" min="0">
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
        = maandbudget ~€${(wbVal * 4).toFixed(0)} · 0 = geen weekbudget
      </small>
    </div>` : ''}
    <div class="modal-actions">
      <button onclick="cancelEditCat()">Annuleer</button>
      <button class="bsave" onclick="saveCat('${c.id}')">Opslaan</button>
    </div>
    ${!c.locked ? `<div style="margin-top:1.5rem;padding-top:1rem;border-top:.5px solid var(--color-border-tertiary)">
      <button class="bdel" style="width:100%" onclick="delCat('${c.id}')">
        ${icon('trash', '', 13)} Categorie verwijderen
      </button>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:6px">
        Items in deze categorie verhuizen automatisch naar "Andere".
      </small>
    </div>` : ''}`;
}

function viewCatTitle() {
  const ec = state.editingCat;
  if (ec === '__new_uit') return '+ Nieuwe uitgaven-categorie';
  if (ec === '__new_in')  return '+ Nieuwe inkomsten-categorie';
  const c = (state.S.cats || []).find(x => x.id === ec);
  return c ? `${c.emoji} ${c.label}` : 'Categorie';
}

/** Backup & export-sectie — één klik download + import van eerder export. */
function viewBackupExport() {
  return `
    <div class="field">
      <label>Exporteren</label>
      <small style="color:var(--color-text-secondary);font-size:12px;display:block;line-height:1.5;margin:4px 0 10px">
        Een lokaal bestand met al je Florijn-data. Bewaar 't ergens veilig — je kan het later importeren om alles te herstellen. Bevat geen data van je medebewoner; die exporteert zelf.
      </small>
      <button class="badd" onclick="downloadExport()">⬇ Download mijn data</button>
    </div>
    <div class="field" style="margin-top:1.25rem;padding-top:1rem;border-top:1px solid var(--color-border-tertiary)">
      <label>Importeren</label>
      <small style="color:var(--color-text-secondary);font-size:12px;display:block;line-height:1.5;margin:4px 0 10px">
        ⚠️ Let op: dit <strong>overschrijft</strong> alle huidige data in jouw Supabase-account. Eventuele wijzigingen sinds de export gaan verloren. Bedoeld voor noodgevallen (account wegvallen, accidentele wipe).
      </small>
      <button class="bdel" onclick="openImportPicker()">📥 Importeer een backup-bestand</button>
    </div>
    <div class="field" style="margin-top:1.25rem;padding-top:1rem;border-top:1px solid var(--color-border-tertiary)">
      <small style="color:var(--color-text-tertiary);font-size:11px;line-height:1.45">
        Export-bestand heet <code>florijn-export-{username}-{YYYY-MM-DD}.json</code>. Alles gebeurt 100% in je browser; niets naar onze servers.
      </small>
    </div>`;
}

function viewHandleiding() {
  // Eén lange pagina met <details>-secties zodat alles scannable blijft.
  // Bewust geen aparte JS — open/dicht is native browser-gedrag.
  const sec = (title, body, open = false) => `
    <details class="hd-section"${open ? ' open' : ''}>
      <summary class="hd-summary">${title}</summary>
      <div class="hd-body">${body}</div>
    </details>`;

  return `
    <div class="hd-intro">
      <p>Florijn is een huishoudbudget-app voor jou (en optioneel je medebewoner). Je werkt met <strong>cycli</strong> die lopen van loondag tot loondag — geen kalendermaand. Binnen elke cyclus zet je <strong>weekbudgetten</strong>, houd je <strong>vaste lasten</strong>, <strong>variabele kosten</strong>, <strong>sparen</strong> en <strong>dromen</strong> apart, en sluit je af met een korte <strong>reflectie</strong>.</p>
    </div>

    ${sec('🧭 De navigatie', `
      <p>Florijn heeft vier hoofd-tabs per persoon:</p>
      <ul>
        <li><strong>🏠 Home</strong> — dashboard van de actieve cyclus.</li>
        <li><strong>💰 Inkomsten</strong> — wat er deze cyclus binnenkomt.</li>
        <li><strong>💸 Uitgaven</strong> — met sub-tabs <em>Extra</em>, <em>Vaste</em>, <em>Variabele</em>.</li>
        <li><strong>🎯 Dromen</strong> — je spaardoelen.</li>
      </ul>
      <p>Op je <strong>gsm</strong> zit deze navigatie als <strong>balk onderaan</strong>; op een <strong>laptop/tablet</strong> verschijnt ze als sidebar links. In <strong>Samen-modus</strong> zie je 2 tabs: <em>Overzicht</em> en <em>Uitgaven</em> (zonder Extra-sub).</p>
      <p>Bovenaan rechts vind je drie knoppen:</p>
      <ul>
        <li><strong>Naam-dropdown</strong> — switch tussen jezelf, je medebewoner (als hij je laat kijken), en Samen.</li>
        <li><strong>🔔 Bel</strong> — meldingen-paneel: loon binnen, reflectie-prompt, gezamenlijke storting, abonn-check, enz.</li>
        <li><strong>⚙️ Tandwiel</strong> — instellingen.</li>
      </ul>
    `, true)}

    ${sec('🏠 Home — je dashboard', `
      <p>Het <strong>Home</strong>-tabblad is waar je dagelijks landt. Belangrijkste tegels:</p>
      <ul>
        <li><strong>Week-tegel</strong> — wat je deze week nog over hebt voor variabele kosten. Bespaarde euro's uit vorige weken in dezelfde cyclus tellen mee.</li>
        <li><strong>Cyclus-overzicht</strong> — inkomsten, vaste lasten, planning sparen, en hoeveel "vrij" geld er per week overblijft.</li>
        <li><strong>Sparen-tegel</strong> — vink af zodra je het bedrag effectief naar de spaarrekening hebt overgezet. Tap "Deze maand niet sparen" als 't even niet lukt.</li>
      </ul>
    `)}

    ${sec('➕ De zwevende +-knop', `
      <p>De ronde <strong>+</strong> rechtsonder (of linksonder, zie hieronder) staat op <em>elk</em> tabblad. Hij doet altijd hetzelfde basisding: een nieuwe transactie toevoegen — maar op de context-tabs vraagt hij eerst kort wat je bedoelt:</p>
      <ul>
        <li>Op <strong>🎯 Dromen</strong>: <em>nieuwe transactie</em> of <em>nieuwe droom</em>?</li>
        <li>Op <strong>💰 Inkomsten</strong>: <em>nieuwe transactie</em> of <em>nieuwe inkomst</em>?</li>
        <li>Op <strong>Vaste</strong> of <strong>Variabele</strong>: <em>nieuwe transactie</em> of <em>nieuw plan-item</em>?</li>
        <li>Op <strong>Home</strong>, <strong>Extra</strong> en <strong>Samen-overzicht</strong> springt hij meteen naar de transactie-modal.</li>
      </ul>
      <p>Linkshandig? Via <strong>Instellingen → 👤 User → + knop links of rechts</strong> verhuis je hem naar de andere kant. Deze keuze blijft per toestel — je gsm en pc hoeven niet hetzelfde te zijn.</p>
    `)}

    ${sec('📅 Cyclus & loondag', `
      <p>Een Florijn-cyclus is je <strong>persoonlijke maand</strong> — van loondag tot loondag. Wanneer je nieuw loon binnenkrijgt:</p>
      <ol>
        <li>Florijn detecteert het en zet een <strong>"Loon binnen?"</strong>-melding in je bel (🔔).</li>
        <li>Vink 'm af → de oude cyclus wordt afgesloten en in je geschiedenis gezet.</li>
        <li>Een nieuwe cyclus start vandaag, weekbudgetten beginnen op nul.</li>
      </ol>
      <p>Verwachte loonbedrag en -dag stel je in via <strong>Instellingen → 💰 Cashflow</strong>. De wekelijkse verdeling voor variabele kosten via <strong>Instellingen → 🍱 Weekbudgetten</strong>.</p>
    `)}

    ${sec('💳 Transacties — toevoegen & bewerken', `
      <p>Een transactie is élke beweging van je geld in een cyclus: een uitgave, een meevaller, een variabele inkomst. Via de zwevende +-knop kom je in de <strong>"Nieuwe transactie"</strong>-modal, met deze keuzes:</p>
      <ul>
        <li><strong>↓ Uitgave</strong> of <strong>↑ Inkomst</strong> — segmentknop bovenaan.</li>
        <li><strong>Rekening</strong> — kiest waar het geld vandaan komt (uitgave) of naartoe gaat (inkomst). Drie opties; details hieronder.</li>
        <li><strong>Bedrag, categorie, omschrijving, datum</strong> — een eigen datum-veld laat je terugboeken naar een dag in deze of vorige week.</li>
        <li><strong>Begroot / Niet begroot</strong> (alleen bij uitgaven) — segmentknop net onder de categorie. Op <em>Begroot</em> koppel je de uitgave aan een gepland item (vast of variabel) — Florijn herkent welk planned-item bij je categorie hoort, en als er maar één match is wordt 't automatisch aangevinkt. Op <em>Niet begroot</em> wordt 't een losse Extra die aan je vrije ruimte knaagt.</li>
      </ul>
      <p><strong>De rekening-knoppen bij Uitgave</strong> = uit welke bron gaat het geld:</p>
      <ul>
        <li><strong>Eigen</strong> — van je persoonlijke rekening. Verlaagt je resterend deze cyclus.</li>
        <li><strong>Gemeenschappelijk</strong> — van de samen-rekening. Optioneel <em>"Geleend"</em>-vinkje: dan moet je het bedrag later terugbetalen aan Samen.</li>
        <li><strong>Spaar</strong> — uit je spaarpot. Vermindert je dromen.gesp proportioneel; KPI blijft netto neutraal (de uitgave + de "uit spaarpot"-inkomst heffen elkaar op).</li>
      </ul>
      <p><strong>De rekening-knoppen bij Inkomst</strong> = waar gaat het binnenkomende geld naartoe:</p>
      <ul>
        <li><strong>Loon</strong> — naar je lopende maand. Verhoogt je resterend deze cyclus. Bruikbaar voor je salaris, een refund, een cadeau, of bewust "ik smijt er even €X bij want het is een krap-maand". Optioneel <em>belasting-vinkje</em> voor belastbare bronnen (flexi-job, bijverdienste) — Florijn reserveert dan automatisch het opgegeven percentage als uitgave "Belastingen".</li>
        <li><strong>Gemeenschappelijk</strong> — twee mogelijkheden, afhankelijk van het vinkje <em>"Ik haal dit van Samen en betaal het later terug"</em>:
          <ul>
            <li><strong>Vink UIT</strong> (default) → je stort het bedrag <em>op</em> de samen-rekening (refund, extra bijdrage). Geen schuld.</li>
            <li><strong>Vink AAN</strong> → je <em>haalt</em> geld van de gemeenschappelijke rekening voor je eigen lopende maand. Bedrag bumpt je resterend deze cyclus én er komt automatisch een schuld "ik moet Samen terugbetalen" op je Schulden-tegel. Handig wanneer een eigen-rekening even krap is en de gezamenlijke pot wel ruimte heeft.</li>
          </ul>
        </li>
        <li><strong>Spaar</strong> — recht naar je dromen. Het geld "verdwijnt" voor je cyclus (resterend blijft gelijk) en groeit proportioneel je spaardoelen aan. Handig voor onverwachte stortingen die je meteen wil wegzetten zonder dat ze als "te besteden geld" voelen.</li>
      </ul>
      <p>Bij een niet-maandelijks plan-item (kwartaal/jaar) verschijnt nog de vraag <em>"Is dit dé betaling deze cyclus?"</em>. Aanvinken = de sinking-fund-teller resetten en eventuele over- of tekort-bedragen netjes verwerken (zie Sparen & dromen).</p>
      <p><strong>Bewerken/verwijderen</strong> doe je via swipe (mobiel) of klik op het pen-/prullenbak-icoontje (desktop) op de rij:</p>
      <ul>
        <li><strong>Vaste, variabele en inkomsten-items</strong> — naam, bedrag, frequentie, categorie, anker-toggle. Verwijderen wist het item maar laat de geboekte uitgaven in de geschiedenis staan.</li>
        <li><strong>Extra-uitgaven</strong> — bedrag, omschrijving, categorie, datum én een <strong>"Gekoppeld aan"</strong>-veld: hier kan je achteraf alsnog de Begroot-link zetten (of weghalen) op een entry waar je 't bij toevoegen vergat.</li>
        <li><strong>Variabele inkomsten</strong> (de losse meevallers deze cyclus, op het Inkomsten-tabblad) — zelfde edit/delete-flow als bij Extra-uitgaven.</li>
      </ul>
    `)}

    ${sec('💸 Vast & Variabel', `
      <p>Twee soorten plan-uitgaven, beide te beheren via <strong>Uitgaven → Vaste</strong> of <strong>Variabele</strong>:</p>
      <ul>
        <li><strong>Vaste</strong> — abonnementen, huur, vaste rekeningen. Geven een frequentie aan (maandelijks, jaarlijks, …); Florijn rekent ze om naar een maandbedrag.</li>
        <li><strong>Variabele</strong> — boodschappen, eten buiten, kleding. Krijgen weekbudgetten zodat je ziet of je on-track bent.</li>
        <li><strong>Extra</strong> (sub-tab onder Uitgaven, alleen persoonlijke modus) — alle losse, niet-geplande mutaties van deze cyclus. Hier zie je je off-plan dingen én meevallers terug.</li>
      </ul>
      <p>Elk item hoort bij een <strong>categorie</strong>. Nieuwe categorieën maak je via <strong>Instellingen → 📤 Categorieën — Uitgaven</strong> (of Inkomsten).</p>
      <p><strong>Per-item progressie-balkje</strong>: bij maandelijkse items toont Florijn "€X / €Y deze cyclus" zodra je een Begrote uitgave boekt. Voor niet-maandelijkse items (kwartaal/jaar) krijg je het <strong>sinking-fund-balkje</strong>: zet 't expliciet aan met <em>"▶ Start vanaf nu"</em> in de itemrij, en Florijn berekent vanaf dan hoeveel je opzij hoort te hebben tegen de volgende betaling — inclusief een rood label als er achterstand is.</p>
    `)}

    ${sec('🍱 Weekbudgetten', `
      <p>Per categorie zet je een wekelijks plafond. Op Home zie je voor de huidige week:</p>
      <ul>
        <li>Je <strong>cap</strong> (wat je had voorzien).</li>
        <li>Wat je al <strong>besteed</strong> hebt.</li>
        <li>Wat er nog <strong>over</strong> is — plus wat je vorige weken bespaard hebt binnen dezelfde cyclus.</li>
      </ul>
      <p>Ga je over je cap? Dat verschijnt als een <em>off-plan extra</em> en knabbelt aan je vrije ruimte. Blijf je eronder? Dat surplus mag je later gebruiken.</p>
    `)}

    ${sec('💰 Sparen & dromen', `
      <p><strong>Sparen</strong> is wat je elke cyclus vast wil wegzetten — stel het bedrag in bij je inkomsten. <strong>Dromen</strong> (eigen tabblad 🎯) definieert waarvoor: een reis, nieuwe laptop, noodfonds. Elke droom krijgt een <em>gewicht</em>: hoe meer gewicht, hoe sneller die droom doorgroeit.</p>
      <p>Elke droom kan je een <strong>einddatum</strong> meegeven (deadline) en pauzeren via swipe-edit. Een lege deadline weghalen en pauze terugzetten werken volledig over een refresh heen — Florijn onthoudt 't.</p>
      <p>Geen ruimte deze maand? Tap <strong>"Deze maand niet sparen"</strong> op de sparen-tegel. Florijn rekent dan geen schuld op tegen je; alleen je droom-progress pauzeert even, en je weekbudget krijgt extra ruimte voor de rest van de cyclus.</p>
      <p>Een droom als <strong>noodfonds</strong> markeren? Dat geeft 'm prioriteit. <strong>Tijdelijk 100% naar noodfonds</strong> sturen kan ook — handig na een onverwachte uitgave; de oude pct-verdeling van je andere dromen wordt netjes bewaard en bij <em>"herstel droom-verdeling"</em> teruggezet (ook na refresh).</p>
      <p><strong>Sinking-fund tekort</strong> — als je een grote jaarbetaling boekt waar nog niet genoeg voor opzij stond, vraagt Florijn waar het ontbrekende bedrag vandaan komt: uit <em>spaardoelen</em> (proportioneel van je dromen afgetrokken) of <em>huidig loon</em> (geboekt als extra deze cyclus, zodat je resterend voor de maand effectief naar beneden gaat).</p>
    `)}

    ${sec('💳 Schulden', `
      <p>Twee soorten:</p>
      <ul>
        <li><strong>Intern — naar/van medebewoner</strong> (alleen zichtbaar in een huishouden). Voor "ik heb de boodschappen voorgeschoten"-situaties. Beheer ze op de Home-tegel "Schulden".</li>
        <li><strong>Extern</strong> (Instellingen → 💳 Externe schulden). Voor leningen aan/van vrienden of familie. Inclusief afbetalingsplan en maandbedrag — Florijn neemt dat mee in je plan.</li>
      </ul>
    `)}

    ${sec('🪞 Reflectie & abonn-check', `
      <p>Bij het einde van een cyclus zet Florijn een <strong>"Vorige cyclus klaar — 30 seconden reflectie?"</strong>-kaartje in je bel (🔔). Klik erop en je krijgt een korte modal: hoe ging 't, wat wil je onthouden voor volgende keer?</p>
      <p>In diezelfde reflectie pikt Florijn <strong>2 willekeurige van je vaste abonnementen</strong> en vraagt: gebruik je 'm nog? Zo kruip je elk maand een stukje uit ongebruikte kosten zonder ooit een grote audit te moeten doen.</p>
      <p>Oude reflecties terug bekijken kan via de geschiedenis-kaartjes op Home.</p>
    `)}

    ${sec('👥 Huishouden delen', `
      <p>Via <strong>Instellingen → 🏠 Huishouden</strong> maak je er één aan of nodig je je medebewoner uit met een <code>?invite=…</code>-link (kopieer-knopje is er). Zodra hij/zij aansluit:</p>
      <ul>
        <li>De naam-dropdown bovenaan toont een extra <strong>Samen</strong>-knop voor gemeenschappelijke kosten.</li>
        <li>Optioneel: <strong>gezamenlijke rekening</strong> aanzetten in Instellingen. Florijn houdt dan automatisch het saldo en de vinkjes voor "Voorgeschoten voor Samen" bij.</li>
        <li>Persoonlijke cijfers blijven privé. Je medebewoner ziet alleen Samen — tenzij hij/zij expliciet <em>"laat me meekijken"</em> krijgt via de toggle in zijn/haar User-instellingen.</li>
      </ul>
    `)}

    ${sec('⚙️ Modus, thema en startdatum', `
      <p>In <strong>Instellingen → 👤 User</strong> stel je in:</p>
      <ul>
        <li><strong>Modus</strong> — Light of Dark.</li>
        <li><strong>Kleur-thema</strong> — accent-kleur. Standaard, Frietjes, Lowie, Pride (auto-actief in juni en op IDAHOT 🏳️‍🌈).</li>
        <li><strong>App-startdatum</strong> — vanaf wanneer Florijn telt voor "Rest vorige maand".</li>
        <li><strong>"Nog over"-weergave</strong> — vrije ruimte tonen per maand of per week.</li>
        <li><strong>+ knop links of rechts</strong> — zet de zwevende +-knop aan de kant die voor jouw hand het comfortabelst is. Wordt per toestel onthouden.</li>
        <li><strong>Automatische stortingen</strong> — vink Sparen of Gem-storting "via bankapp" zodat de melding niet maandelijks terugkeert.</li>
      </ul>
    `)}

    ${sec('💾 Backup, export & import', `
      <p>Al je data zit op Supabase plus lokaal in je browser-cache. Veiligheidsmaatregel: ga regelmatig naar <strong>Instellingen → 💾 Backup & export</strong> en download een JSON-bestand. Bij dataverlies kun je 'm hier ook weer importeren — let op: <em>import overschrijft alles wat er op dat moment in staat</em>.</p>
      <p>"Reset alle transactie-data" zit onder <strong>⚠️ Gevarenzone</strong> en wist nu écht álles (lokaal + Supabase). Categorieën en instellingen blijven staan.</p>
    `)}

    ${sec('📡 Offline & sync', `
      <p>Florijn werkt offline. Mutaties zonder internet komen in een lokale wachtrij — je ziet een badge "<em>N wijzigingen wacht</em>" naast het logo. Zodra je weer online bent (of de tab opnieuw actief wordt), worden ze automatisch verstuurd.</p>
      <p>Bij elke app-opstart toont Florijn binnen ~300 ms je laatst-gekende state uit de cache, terwijl op de achtergrond verse data uit Supabase wordt gehaald.</p>
      <p><strong>Tussen toestellen</strong> — als je een wijziging op je pc maakt en daarna je gsm openhaalt, ververst Florijn automatisch zodra het tabblad terug focus krijgt (of bij een online-event). Wachtende lokale mutaties gaan eerst nog UP voor de pull begint, zodat je niks verliest. Een open modal of bewerkscherm wordt nooit onderbroken — daar wordt de refresh dan netjes overgeslagen.</p>
    `)}

    ${sec('🔄 App up-to-date houden', `
      <p>Florijn checkt zelf elk uur of er een nieuwe versie klaar staat. Is dat het geval, dan verschijnt rechtsonder een toast <strong>"✨ Nieuwe versie beschikbaar"</strong>. Tap "Vernieuwen" en je zit op de laatste versie — geen herinstallatie meer nodig.</p>
      <p>Twijfel je? Ga naar <strong>Instellingen → 👤 User → App-versie → ↻ Check op updates</strong> voor een handmatige controle.</p>
    `)}

    ${sec('📱 Florijn op je startscherm', `
      <p>Florijn is een <strong>PWA</strong> — een web-app die je kan installeren als ware 't een echte app:</p>
      <ul>
        <li><strong>iPhone (Safari)</strong>: tik op het deel-icoon (vierkant met pijltje omhoog) → <em>"Zet op beginscherm"</em>.</li>
        <li><strong>Android (Chrome)</strong>: menu (⋮) → <em>"App installeren"</em> of <em>"Toevoegen aan startscherm"</em>.</li>
        <li><strong>Desktop (Chrome/Edge)</strong>: install-icoontje in de adresbalk.</li>
      </ul>
      <p>Eens geïnstalleerd opent Florijn full-screen, zonder browser-balk — voelt aan als een echte app.</p>
    `)}

    ${sec('🎓 Rondleiding opnieuw bekijken', `
      <p>De welkomst-tour die je bij je eerste login zag, kan je altijd opnieuw afspelen:</p>
      <button class="badd" onclick="replayOnboarding()" style="margin-top:4px">▶ Start rondleiding opnieuw</button>
    `)}

    <div class="hd-footer">
      <p>Mis je iets? Laat 't de maker weten — Florijn is een hobbyproject en feedback maakt 'm beter.</p>
    </div>
  `;
}

function viewGevarenzone() {
  return `
    <div class="field">
      <button class="bdel" style="width:100%" onclick="resetData()">
        ${icon('trash', '', 13)} Reset alle transactie-data (cats blijven)
      </button>
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:8px">
        Wist al je eigen transactie-data (inkomsten, vaste + variabele kosten, geschiedenis, schulden, dromen, weekbudgetten, sparen-vinkjes) — lokaal én op Supabase. Categorieën en app-instellingen blijven staan. Onomkeerbaar.
      </small>
    </div>`;
}

/** Hoeveel items elke cat heeft (over alle personen + samen). Gebruikt
 *  zowel door de cat-lijst (badge) als door de menu-teller. */
function countCatUsage() {
  const usage = {};
  ['sven', 'lander', 'samen'].forEach(p => {
    const d = state.S[p] || {};
    [...(d.vast || []), ...(d.var || []), ...(d.inkomsten || [])].forEach(it => {
      if (it.cat) usage[it.cat] = (usage[it.cat] || 0) + 1;
    });
    Object.values(d.hist || {}).forEach(h => {
      [...(h.ex || []), ...(h.in || [])].forEach(e => {
        if (e.cat) usage[e.cat] = (usage[e.cat] || 0) + 1;
      });
    });
  });
  return usage;
}

/**
 * Externe-schulden CRUD-sectie in Instellingen. Niet zichtbaar op Samen.
 * Inline edit-state via state.editing['extschuld-' + id].
 */
function externeSchuldenSection() {
  const list = state.S[state.person]?.externeSchulden || [];
  const rows = list.map(s => {
    const editKey = 'extschuld-' + s.id;
    const isEd = !!state.editing[editKey];
    if (isEd) {
      return `<div class="cat-row cat-row-edit" style="flex-wrap:wrap;gap:6px">
        <input type="text"   id="es-n-${s.id}" value="${s.n}" placeholder="Naam" style="flex:1 1 120px">
        <input type="number" id="es-hs-${s.id}" value="${s.hoofdsom}" step="0.01" placeholder="Hoofdsom €" style="width:90px">
        <input type="number" id="es-r-${s.id}" value="${s.rest}" step="0.01" placeholder="Rest €" style="width:90px">
        <input type="number" id="es-rt-${s.id}" value="${s.rente}" step="0.1" placeholder="Rente %" style="width:70px">
        <input type="number" id="es-mm-${s.id}" value="${s.minMaand}" step="1" placeholder="Min €/m" style="width:70px">
        <button class="bsave"   onclick="saveExterneSchuld(${s.id})">Opslaan</button>
        <button class="bcancel" onclick="editExterneSchuld(${s.id})">×</button>
      </div>`;
    }
    return `<div class="cat-row" style="border-left:3px solid #c0392b">
      <span class="cat-emoji">💳</span>
      <span class="cat-label">${s.n}</span>
      <span class="cat-usage">€${(s.rest || 0).toFixed(0)} / €${(s.hoofdsom || 0).toFixed(0)}</span>
      <span class="rente-badge">${(s.rente || 0)}%</span>
      <span class="cat-usage">min €${(s.minMaand || 0).toFixed(0)}/m</span>
      <button class="cat-btn" onclick="editExterneSchuld(${s.id})">✎</button>
      <button class="cat-btn cat-del" onclick="delExterneSchuld(${s.id})">🗑</button>
    </div>`;
  }).join('') || '<div style="font-size:12px;color:var(--color-text-secondary);padding:4px 0">Geen externe schulden — nice 🎉</div>';

  const addForm = `<div class="cat-row cat-row-edit" style="flex-wrap:wrap;gap:6px;margin-top:6px">
    <input type="text"   id="esn-n"  placeholder="Naam (bv. Kredietkaart)" style="flex:1 1 140px">
    <input type="number" id="esn-hs" placeholder="Hoofdsom €" step="0.01" style="width:90px">
    <input type="number" id="esn-r"  placeholder="Rest € (laat leeg = hoofdsom)" step="0.01" style="width:90px">
    <input type="number" id="esn-rt" placeholder="Rente %" step="0.1" style="width:70px">
    <input type="number" id="esn-mm" placeholder="Min €/m" step="1" style="width:70px">
    <input type="date"   id="esn-sd" style="width:130px">
    <button class="bsave" onclick="addExterneSchuld()">Toevoegen</button>
  </div>`;

  return `<div class="set-section">
    <div class="set-section-title">Externe schulden (${nameFor(state.person)})</div>
    ${rows}
    ${addForm}
    <small style="display:block;margin-top:6px;font-size:11px;color:var(--color-text-secondary)">
      Externe leningen (kredietkaart, bank, familie). Aflossingen verschijnen als "Aflossen"-knop op Home.
    </small>
  </div>`;
}

function newCatForm(scope) {
  const showWb = scope === 'uit' && state.person !== 'samen';
  return `<div class="cat-row cat-row-edit" style="margin-top:6px">
    <input type="text" id="nc-emoji" value="📦" maxlength="3" style="width:42px;text-align:center;font-size:18px">
    <input type="text" id="nc-label" placeholder="Naam" style="flex:1">
    <input type="color" id="nc-color" value="#7f8c8d" style="width:32px;height:28px;border:none;cursor:pointer;padding:0;border-radius:5px">
    <button class="bsave" onclick="addCat('${scope}')">Toevoegen</button>
    <button class="bcancel" onclick="cancelEditCat()">×</button>
  </div>
  ${showWb ? `<div class="cat-wb-row">
    <label>Weekbudget voor ${nameFor(state.person)} (€) — optioneel</label>
    <input type="number" id="nc-wb" value="0" step="1" min="0">
    <small>Cats met een weekbudget verschijnen als widget op Home + krijgen een maand-overschrijdings-notif.</small>
  </div>` : ''}`;
}

// -----------------------------------------------------------------
// Add-item modal — focused popup voor vast / var / inkomst / droom
// -----------------------------------------------------------------
// Vervangt de inline-addforms die anders in elke tab uitklapten. Eén
// venster per add-actie houdt de gebruiker bij één ding tegelijk.

export function buildAddItemModal() {
  const m = state.addItemModal;
  if (!m) return '';
  if (m.type === 'vast' || m.type === 'var') return buildAddVastVarBody(m);
  if (m.type === 'inkomst') return buildAddInkomstBody();
  if (m.type === 'droom')   return buildAddDroomBody();
  return '';
}

// -----------------------------------------------------------------
// FAB picker — kleine bottom-sheet die op context-tabs verschijnt
// (Dromen / Inkomsten / Vaste / Variabele) zodat de gebruiker kan
// kiezen tussen een gewone transactie en een nieuw item van het type
// dat bij die tab hoort.
// -----------------------------------------------------------------

export function buildFabPickerModal() {
  const ctx = state.fabPicker;
  if (!ctx) return '';
  return `<div class="fab-pick-overlay" onclick="if(event.target===this) closeFabPicker()">
    <div class="fab-pick" role="dialog" aria-label="Wat wil je toevoegen?">
      <div class="fab-pick-title">Wat wil je toevoegen?</div>
      <button class="fab-pick-btn" onclick="fabPick('trans')">
        ${icon('plus', 'color:currentColor', 16)} Nieuwe transactie
      </button>
      <button class="fab-pick-btn alt" onclick="fabPick('item')">
        ${icon('plus', 'color:currentColor', 16)} ${ctx.label}
      </button>
      <button class="fab-pick-cancel" onclick="closeFabPicker()">Annuleren</button>
    </div>
  </div>`;
}

function buildAddVastVarBody(m) {
  const addkey = `aim-${m.type}-${m.person}`;
  const title  = m.type === 'vast' ? '+ Vaste kost toevoegen' : '+ Variabele kost toevoegen';
  const cats   = catsForScope('uit').filter(c => !['sparen', 'extra_sparen', 'terugbet_uit'].includes(c.id));
  return `<div class="modal-overlay" onclick="if(event.target===this) closeAddItem()">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">${title}</div>
        <button class="modal-close" onclick="closeAddItem()">×</button>
      </div>
      <div class="field">
        <label>Naam</label>
        <input type="text" id="${addkey}-n" placeholder="bv. Netflix" autofocus>
      </div>
      <div class="field">
        <label>Bedrag (€)</label>
        <input type="number" id="${addkey}-a" step="0.01" placeholder="0,00">
      </div>
      <div class="field">
        <label>Categorie</label>
        <select id="${addkey}-cat">
          <option value="">— Kies —</option>
          ${cats.map(c => `<option value="${c.id}">${c.emoji} ${c.label}</option>`).join('')}
        </select>
      </div>
      <div class="field">
        <label>Frequentie</label>
        <select id="${addkey}-f" onchange="onFreqChange(this, '${addkey}-fc', '${addkey}-lp-wrap')">
          <option value="1">Maandelijks</option>
          <option value="3">Kwartaal (om de 3 maanden)</option>
          <option value="6">Halfjaarlijks (om de 6 maanden)</option>
          <option value="12">Jaarlijks</option>
          <option value="custom">Anders…</option>
        </select>
        <div id="${addkey}-fc" style="display:none;margin-top:6px;font-size:12px;color:var(--color-text-secondary)">
          Om de <input type="number" id="${addkey}-fcN" min="1" max="60" value="2" style="width:60px"> maanden
        </div>
        <div id="${addkey}-lp-wrap" style="display:none;margin-top:8px;font-size:12px;color:var(--color-text-secondary)">
          <label style="display:block;margin-bottom:3px">Laatst betaald (optioneel — anders vanaf vandaag)</label>
          <input type="month" id="${addkey}-lp" style="font-size:13px">
        </div>
      </div>
      <label class="cb-row cb-block">
        <input type="checkbox" id="${addkey}-dom">
        <span>🔁 Domiciliëring — wordt automatisch van het loon afgehouden</span>
      </label>
      <div class="modal-actions">
        <button onclick="closeAddItem()">Annuleer</button>
        <button class="bsave" onclick="addItem('${m.type}','${m.person}','${addkey}')">Toevoegen</button>
      </div>
    </div>
  </div>`;
}

function buildAddInkomstBody() {
  const key  = 'inkomstenadd';     // hardcoded — addInk() leest deze IDs
  const cats = catsForScope('in').filter(c => !['extra_sparen', 'rest_vorig', 'uit_spaar'].includes(c.id));
  return `<div class="modal-overlay" onclick="if(event.target===this) closeAddItem()">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">+ Vaste inkomst toevoegen</div>
        <button class="modal-close" onclick="closeAddItem()">×</button>
      </div>
      <div class="field">
        <label>Naam</label>
        <input type="text" id="${key}-n" placeholder="bv. Loon" autofocus>
      </div>
      <div class="field">
        <label>Bedrag (€)</label>
        <input type="number" id="${key}-a" step="0.01" placeholder="0,00">
      </div>
      <div class="field">
        <label>Categorie</label>
        <select id="${key}-cat">
          <option value="">— Kies —</option>
          ${cats.map(c => `<option value="${c.id}">${c.emoji} ${c.label}</option>`).join('')}
        </select>
      </div>
      <div class="field">
        <label>Frequentie</label>
        <select id="${key}-f">
          <option value="m">Maandelijks</option>
          <option value="j">Jaarlijks</option>
        </select>
      </div>
      <div class="field">
        <label>Startdag van de maand</label>
        <input type="number" id="${key}-sd" value="1" min="1" max="28" style="width:90px">
      </div>
      <label class="cb-row cb-block">
        <input type="checkbox" id="${key}-anker">
        <span>Anker — dit loon bepaalt de budgetmaand</span>
      </label>
      <div class="modal-actions">
        <button onclick="closeAddItem()">Annuleer</button>
        <button class="bsave" onclick="addInk()">Toevoegen</button>
      </div>
    </div>
  </div>`;
}

function buildAddDroomBody() {
  return `<div class="modal-overlay" onclick="if(event.target===this) closeAddItem()">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">+ Nieuwe droom</div>
        <button class="modal-close" onclick="closeAddItem()">×</button>
      </div>
      <div class="field">
        <label>Naam</label>
        <input type="text" id="dn" placeholder="bv. Vakantie" autofocus>
      </div>
      <div class="field">
        <label>Doelbedrag (€)</label>
        <input type="number" id="dd" step="0.01" placeholder="bv. 2000">
      </div>
      <div class="field">
        <label>Al gespaard (€)</label>
        <input type="number" id="dg" step="0.01" value="0">
      </div>
      <div class="field">
        <label>% van spaarcenten</label>
        <input type="number" id="dp" value="50" min="0" max="100" style="width:90px">
      </div>
      <div class="field">
        <label>Deadline (optioneel)</label>
        <input type="date" id="dl">
      </div>
      <div class="field">
        <label>Kleur</label>
        <input type="color" id="dc" value="#2a9d5a" style="width:60px;height:36px;border:none;cursor:pointer;padding:0;border-radius:6px">
      </div>
      <div class="modal-actions">
        <button onclick="closeAddItem()">Annuleer</button>
        <button class="bsave" onclick="addDroom()">Toevoegen</button>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Toevoeg-modal — In/Uit, bedrag, categorie, notitie, rekening, datum
// -----------------------------------------------------------------

/** YYYY-MM-DD van vandaag in local time — voor <input type="date"> value/max. */
function todayIso() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/**
 * Begroot/Niet-begroot-toggle in de add-modal, inclusief dropdown om uit
 * meerdere matchende items te kiezen en — voor niet-maandelijkse items —
 * een vinkje "Is dit dé betaling deze cyclus?".
 *
 * Wordt alleen getoond bij Uit + eigen (dir='uit' && rek='eigen'). Voor
 * andere combinaties (inkomst, samen, spaar) is begroot/niet-begroot
 * niet van toepassing.
 */
function buildBegrootSection(dir, rek) {
  if (dir !== 'uit' || rek !== 'eigen') return '';
  const d = state.S[state.person];
  if (!d) return '';

  // Cat-keuze leest van state.modalCat — die wordt onderhouden door
  // onModalCatChange en door renderModal (DOM → state sync vóór innerHTML).
  // Het DOM-element bestaat nog niet wanneer buildAddModal voor de eerste
  // keer draait, dus DOM-lezen werkt hier niet betrouwbaar.
  const catId = state.modalCat || '';
  const vast = (d.vast || [])
    .map((it, i) => ({ ...it, __type: 'vast', __i: i }))
    .filter(it => it.cat === catId);
  const vari = (d.var || [])
    .map((it, i) => ({ ...it, __type: 'var', __i: i }))
    .filter(it => it.cat === catId);
  const matches = [...vast, ...vari];

  // Geen cat gekozen of geen matches → toggle is irrelevant; entry wordt
  // sowieso off-plan. We tonen wel een korte hint zodat de gebruiker weet
  // waarom de keuze er niet is.
  if (matches.length === 0) {
    if (!catId) return '';
    return `
      <div class="field">
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;line-height:1.45">
          Geen begroot item voor deze categorie — deze uitgave telt als off-plan extra (eet aan je weekbedrag).
        </small>
      </div>`;
  }

  const mode = state.modalBegrootMode || 'begroot';
  const segMode = `<div class="seg">
    <button type="button" class="${mode === 'begroot' ? 'active' : ''}" onclick="setModalBegrootMode('begroot')">📦 Begroot</button>
    <button type="button" class="${mode === 'niet'    ? 'active' : ''}" onclick="setModalBegrootMode('niet')">✨ Niet begroot</button>
  </div>`;

  // Picker / readonly tekst voor welk item er aangesproken wordt.
  let pickerHTML = '';
  let payCheckHTML = '';
  let helpHTML = '';

  if (mode === 'begroot') {
    const key = state.modalBegrootItemKey
      || `${matches[0].__type}:${matches[0].__i}`;
    const picked = matches.find(m => `${m.__type}:${m.__i}` === key) || matches[0];

    if (matches.length === 1) {
      pickerHTML = `
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:6px">
          Wordt afgetrokken van: <strong>${picked.n}</strong> (€${picked.a.toFixed(2)}${freqM(picked) === 1 ? '/mnd' : ' totaal'})
        </small>`;
    } else {
      const opts = matches.map(m => {
        const k = `${m.__type}:${m.__i}`;
        const fm = freqM(m);
        const lbl = `${m.n} — €${m.a.toFixed(2)}${fm === 1 ? '/mnd' : ` om de ${fm} mnd`}`;
        return `<option value="${k}" ${k === key ? 'selected' : ''}>${lbl}</option>`;
      }).join('');
      pickerHTML = `
        <label style="font-size:11px;color:var(--color-text-secondary);display:block;margin:6px 0 3px">Welk potje?</label>
        <select onchange="setModalBegrootItem(this.value)">
          ${opts}
        </select>`;
    }

    if (freqM(picked) > 1) {
      payCheckHTML = `
        <label class="cb-row cb-block" style="margin-top:8px">
          <input type="checkbox" ${state.modalIsDePayment ? 'checked' : ''}
                 onchange="setModalIsDePayment(this.checked)">
          <span>Dit is dé betaling deze cyclus (reset de teller)</span>
        </label>
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
          Aanvinken als dit de officiële betaling is van <strong>${picked.n}</strong>. Een tussentijdse uitgave die "ergens bij hoort" laat je uitgevinkt.
        </small>`;
    }
  } else {
    helpHTML = `
      <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:6px;line-height:1.45">
        Telt als off-plan extra: gaat van je weekbedrag af, raakt de begrote balk niet.
      </small>`;
  }

  return `
    <div class="field">
      <label>Begroot of niet?</label>
      ${segMode}
      ${pickerHTML}
      ${payCheckHTML}
      ${helpHTML}
    </div>`;
}

export function buildAddModal() {
  if (!state.modalOpen) return '';

  const dir = state.modalDir;          // 'uit' | 'in'
  const rek = state.modalRek;          // 'eigen' | 'samen' | 'spaar'
  const cats = catsForScope(dir).filter(c => !['sparen', 'rest_vorig', 'terugbet_uit'].includes(c.id));

  const segDir = `<div class="seg">
    <button class="${dir === 'uit' ? 'active' : ''}" onclick="setModalDir('uit')">↓ Uitgave</button>
    <button class="${dir === 'in'  ? 'active' : ''}" onclick="setModalDir('in')">↑ Inkomst</button>
  </div>`;

  // Toon de Samen-rekening (en het bijhorende leen/voorschot-vinkje) alleen
  // wanneer het huishouden ook effectief een gezamenlijke rekening heeft.
  // Anders is "Gemeenschappelijk" niet logisch en moet ze niet aanklikbaar zijn.
  const showSamen        = state.hasSharedAccount;
  const showMatePartner  = state.mateConnected;
  // Bij inkomsten zegt "Loon" duidelijker waar het geld heen gaat (de
  // lopende cyclus) dan het neutrale "Eigen". Bij uitgaven blijft "Eigen"
  // de juiste term (uit welke bron komt het: eigen rekening, samen, spaar).
  const eigenLbl = dir === 'in' ? 'Loon' : 'Eigen';
  const segRek = `<div class="seg">
    <button class="${rek === 'eigen' ? 'active' : ''}" onclick="setModalRek('eigen')">${eigenLbl}</button>
    ${showSamen ? `<button class="${rek === 'samen' ? 'active' : ''}" onclick="setModalRek('samen')">Gemeenschappelijk</button>` : ''}
    <button class="${rek === 'spaar' ? 'active' : ''}" onclick="setModalRek('spaar')">Spaar</button>
  </div>`;

  // Conditional vinkjes
  let vinkje = '';
  // Belasting-reservering (Fase D, BE). Standaard UIT — gebruiker vinkt aan
  // voor belastbare bronnen zoals flexi-job of bijverdienste. Niet voor loon
  // (al netto) of maaltijdcheques (vrijgesteld). Enkel relevant op Eigen:
  // bij Samen of Spaar landt het geld niet op je belastbare cyclus.
  if (dir === 'in' && rek === 'eigen' && state.person !== 'samen') {
    const bp = state.S[state.person]?.belastingPct ?? 25;
    vinkje += `<div class="lend-row" style="flex-direction:column;align-items:stretch;gap:6px">
      <label class="cb-row cb-block">
        <input type="checkbox" id="md-bel">
        <span>🏛️ Reserveer voor belasting (vink aan bij flexi-job, bijverdienste, …)</span>
      </label>
      <div style="display:flex;align-items:center;gap:6px;font-size:12px;color:var(--color-text-secondary);padding-left:22px">
        <input type="number" id="md-bel-pct" value="${bp}" min="0" max="100" step="1" style="width:60px;font-size:12px">
        <span>% van het bedrag — wordt direct als uitgave "Belastingen" geboekt</span>
      </div>
    </div>`;
  }
  if (dir === 'in' && rek === 'samen' && showSamen) {
    vinkje += `<label class="lend-row">
      <input type="checkbox" id="md-leen-van-samen">
      Ik haal dit van Samen en betaal het later terug
    </label>`;
  }
  if (dir === 'uit' && rek === 'samen' && showSamen) {
    vinkje += `<label class="lend-row">
      <input type="checkbox" id="md-leen">
      Geleend — moet terugbetaald worden aan Samen
    </label>`;
  } else if (dir === 'uit' && rek === 'eigen') {
    // Voorgeschoten voor Samen: enkel zinvol als er ook écht een
    // gezamenlijke rekening bestaat.
    if (showSamen) {
      vinkje = `<label class="lend-row">
        <input type="checkbox" id="md-voorschot">
        Voorgeschoten voor Samen — Samen betaalt me terug
      </label>`;
    }
    // Voorgeschoten voor de partner: enkel zinvol als er een medebewoner
    // is aangesloten op het huishouden.
    if (showMatePartner) {
      const otherKey = state.person === 'sven' ? 'lander'
                     : state.person === 'lander' ? 'sven' : null;
      const other = otherKey ? nameFor(otherKey) : null;
      if (other) {
        vinkje += `<label class="lend-row">
          <input type="checkbox" id="md-voorschot-andere">
          Voorgeschoten voor ${other} — ${other} betaalt me terug
        </label>`;
      }
    }
  }

  const catOptions = cats.map(c => `<option value="${c.id}">${c.emoji} ${c.label}</option>`).join('');

  return `<div class="modal-overlay" onclick="if(event.target===this) closeAdd()">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">Nieuwe transactie</div>
        <button class="modal-close" onclick="closeAdd()">×</button>
      </div>

      ${segDir}

      <div class="field">
        <label>Bedrag (€)</label>
        <input type="number" id="md-amt" step="0.01" placeholder="0,00" autofocus>
      </div>

      <div class="field">
        <label>Categorie</label>
        <select id="md-cat" onchange="onModalCatChange(this.value)">
          <option value="">— Kies —</option>
          ${catOptions}
        </select>
      </div>

      ${buildBegrootSection(dir, rek)}

      <div class="field">
        <label>Notitie (optioneel)</label>
        <input type="text" id="md-desc" placeholder="bv. Colruyt vrijdag">
      </div>

      <div class="field">
        <label>Datum</label>
        <input type="date" id="md-date" value="${todayIso()}" max="${todayIso()}">
        <small style="color:var(--color-text-secondary);font-size:11px;display:block;margin-top:3px">
          Standaard vandaag. Past pas in als je een uitgave van een paar dagen geleden inhaalt — telt dan ook bij die week / die cyclus.
        </small>
      </div>

      <div class="field">
        <label>Rekening</label>
        ${segRek}
      </div>

      ${vinkje}

      <div class="modal-actions">
        <button onclick="closeAdd()">Annuleer</button>
        <button class="bsave" onclick="saveAdd()">Toevoegen</button>
      </div>
    </div>
  </div>`;
}

/**
 * Submodal: vraagt of een uitgave de begrote betaling is voor een planned
 * item, of een extra uitgave bovenop het budget. Toont state.pendingAdd
 * (gezet door saveAdd wanneer er een matching planned item gevonden is).
 */
export function buildBegrootModal() {
  const p = state.pendingAdd;
  if (!p) return '';
  const phase = p.phase || 'pick';
  if (phase === 'pick')      return renderPickPhase(p);
  if (phase === 'overshoot') return renderOvershootPhase(p);
  if (phase === 'tekort')    return renderTekortPhase(p);
  return '';
}

function renderPickPhase(p) {
  const catMeta = catById(p.cat);
  const opts = p.matches.map((m, idx) => {
    const dom = m.domicilie ? ' 🔁' : '';
    const fM = m.freqM ?? (m.f === 'j' ? 12 : 1);
    const freqLbl = fM === 1
      ? `€${m.a.toFixed(2)}/mnd`
      : fM === 12
        ? `€${(m.a / 12).toFixed(2)}/mnd (jaarlijks €${m.a.toFixed(2)})`
        : `€${(m.a / fM).toFixed(2)}/mnd (om de ${fM} mnd €${m.a.toFixed(2)})`;
    return `<option value="${idx}">${m.n} — ${freqLbl}${dom}</option>`;
  }).join('');

  return `<div class="modal-overlay" onclick="if(event.target===this) confAdd('ann')">
    <div class="modal" style="max-width:420px">
      <div class="modal-head">
        <div class="modal-title">Welke uitgave is dit?</div>
        <button class="modal-close" onclick="confAdd('ann')">×</button>
      </div>
      <p style="font-size:13px;color:var(--color-text-secondary);margin-bottom:.85rem">
        €${p.amt.toFixed(2)} voor categorie <strong>${catMeta.emoji} ${catMeta.label}</strong>
      </p>
      <div class="field">
        <label>Gepland item</label>
        <select id="md-bk-pick">
          <option value="">— Kies —</option>
          ${opts}
          <option value="extra">🆕 Niet gepland — extra uitgave</option>
        </select>
        <small style="display:block;margin-top:4px;font-size:11px;color:var(--color-text-secondary)">
          🔁 = domiciliëring (wordt automatisch betaald)
        </small>
      </div>
      <div class="modal-actions">
        <button onclick="confAdd('ann')">Annuleer</button>
        <button class="bsave" onclick="confAdd('pick')">Bevestig</button>
      </div>
    </div>
  </div>`;
}

function renderOvershootPhase(p) {
  const item = p.pickedItem;
  const overshoot = p.amt - item.a;
  return `<div class="modal-overlay" onclick="if(event.target===this) confAdd('ann')">
    <div class="modal" style="max-width:420px">
      <div class="modal-head">
        <div class="modal-title">Prijs gestegen?</div>
        <button class="modal-close" onclick="confAdd('ann')">×</button>
      </div>
      <p style="font-size:13px;color:var(--color-text-primary);margin-bottom:6px">
        Je betaalt <strong>€${p.amt.toFixed(2)}</strong> voor <strong>${item.n}</strong>, terwijl je <strong>€${item.a.toFixed(2)}</strong> gepland had.
      </p>
      <p style="font-size:12px;color:var(--color-text-secondary);margin-bottom:1rem">
        €${overshoot.toFixed(2)} meer dan begroot. Is de prijs structureel gestegen, of eenmalig hoger?
      </p>
      <div class="modal-actions" style="flex-direction:column;gap:6px">
        <button class="bsave" onclick="confAdd('higher')">Prijs gestegen — bedrag automatisch aanpassen naar €${p.amt.toFixed(2)}</button>
        <button onclick="confAdd('one-off')">Eenmalig hoger — overschot boeken als extra</button>
        <button class="bcancel" onclick="confAdd('ann')">Annuleer</button>
      </div>
    </div>
  </div>`;
}

function renderTekortPhase(p) {
  const item = p.pickedItem;
  const tekort = Math.max(0, p.amt - (p.savedSF || 0));
  const d = state.S[state.person];
  const dromenTotal = (d.dromen || []).reduce((s, dr) => s + (dr.gesp || 0), 0);
  const dromenOk = dromenTotal >= tekort;

  return `<div class="modal-overlay" onclick="if(event.target===this) confAdd('ann')">
    <div class="modal" style="max-width:420px">
      <div class="modal-head">
        <div class="modal-title">Tekort — waar uit halen?</div>
        <button class="modal-close" onclick="confAdd('ann')">×</button>
      </div>
      <p style="font-size:13px;color:var(--color-text-primary);margin-bottom:6px">
        Je hebt <strong>€${(p.savedSF || 0).toFixed(2)}</strong> opgebouwd voor <strong>${item.n}</strong>, maar betaalt <strong>€${p.amt.toFixed(2)}</strong>.
      </p>
      <p style="font-size:12px;color:var(--color-text-secondary);margin-bottom:1rem">
        Tekort: <strong>€${tekort.toFixed(2)}</strong>. Waar wil je het uit halen?
      </p>
      <div class="modal-actions" style="flex-direction:column;gap:6px">
        <button class="bsave" onclick="confAdd('loon')">Huidig loon — telt als extra deze cyclus</button>
        <button onclick="confAdd('dromen')" ${!dromenOk ? 'disabled' : ''}>
          Spaardoelen — verdeeld over je dromen (totaal beschikbaar: €${dromenTotal.toFixed(2)})
        </button>
        <button class="bcancel" onclick="confAdd('ann')">Annuleer</button>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Weekly-budget widget (groceries / going out)
// -----------------------------------------------------------------

/**
 * Week-budget-widget voor één categorie. Uitgegeven wordt afgeleid uit
 * hist.ex (deze week) — geen aparte counter meer.
 */
export function buildWW(catId) {
  const cmeta = catById(catId);
  const week  = weekBudgetFor(state.person, catId);
  const spent = spentThisWeek(state.person, catId);
  const pct   = week > 0 ? Math.min(110, spent / week * 100) : 0;
  const rest  = week - spent;
  const c     = pct > 100 ? '#c0392b' : cmeta.color;
  const restTxt = rest >= 0
    ? `€${rest.toFixed(2)} over`
    : `€${Math.abs(rest).toFixed(2)} OVER BUDGET ⚠️`;

  return `<div class="ww">
    <div class="whead">
      <span class="wlbl">${cmeta.emoji} ${cmeta.label} — week</span>
      <button class="webtn" onclick="toggleWE('${catId}')">Aanpassen</button>
    </div>
    <div class="wbarbg"><div class="wbarfill" style="width:${Math.min(100, pct)}%;background:${c}"></div></div>
    <div class="wstats">
      <span>Uitgegeven: <strong>€${spent.toFixed(2)}</strong></span>
      <span style="font-weight:500;color:${c}">${restTxt}</span>
    </div>
    <div class="edit-box edit-box-inline" id="we-${catId}" style="display:none">
      <label>Weekbudget (€)</label>
      <input type="number" id="wb-${catId}" value="${week}" step="1">
      <div class="ebtns">
        <button class="bcancel" onclick="toggleWE('${catId}')">Annuleer</button>
        <button class="bsave"   onclick="saveWE('${catId}')">Opslaan</button>
      </div>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Samen — overview of shared household
// -----------------------------------------------------------------

export function buildSamenOverzicht() {
  const seg = samenSegs();
  const tot = seg.totaal || 1;

  // Read-only schulden-overzicht: wie heeft van Samen geleend?
  const schulden = samenSchulden();
  const schuldenCard = schulden.length
    ? `<div class="schuld-section">
        <div class="card-title" style="margin-bottom:6px">⚖️ Openstaande leningen aan Samen</div>
        ${schulden.map(s => {
          const pct = s.bedrag > 0 ? Math.min(100, (s.bedrag - s.rest) / s.bedrag * 100) : 0;
          return `<div class="schuld-item"><div class="schuld-row">
            <div class="schuld-info">
              <div>→ ${s.desc || s.cat || 'Lening'}</div>
              <div class="schuld-meta">${nameFor(s.persoon)} moet terugbetalen · ${s.date}</div>
              <div class="schuld-progress-bg">
                <div class="schuld-progress-fill" style="width:${pct}%"></div>
              </div>
              <div class="schuld-progress-meta">€${(s.bedrag - s.rest).toFixed(2)} / €${s.bedrag.toFixed(2)} afbetaald (${Math.round(pct)}%)${s.termijn ? ` · plan: €${s.termijn}/mnd` : ''}</div>
            </div>
            <div class="schuld-actions">
              <span class="schuld-amt">€${s.rest.toFixed(2)}</span>
            </div>
          </div></div>`;
        }).join('')}
      </div>`
    : '';

  return `<div class="sbadge">
      ${icon('users', '', 13)} Gemeenschappelijke rekening
    </div>
    <div class="kpi2">
      <div class="kpi">
        <div class="kpilbl">Totaal/maand</div>
        <div class="kpival">€${Math.round(seg.totaal)}</div>
      </div>
      <div class="kpi">
        <div class="kpilbl">Per persoon</div>
        <div class="kpival">€${Math.round(seg.totaal / 2)}</div>
      </div>
    </div>
    ${schuldenCard}
    <div class="card">
      <div class="card-title">Uitgaven per categorie</div>
      <div class="pie-wrap">
        <canvas id="pieSamen" width="130" height="130"></canvas>
        <div class="pie-leg">
          ${themeRecolor(seg.cats).map(c => `
            <div class="lrow">
              <div class="ldot" style="background:${c.color}"></div>
              <span class="llbl">${c.emoji} ${c.label}</span>
              <span class="lpct">${Math.round(c.amount / tot * 100)}%</span>
            </div>`).join('') || '<div style="font-size:12px;color:var(--color-text-secondary)">Geen gemeenschappelijke uitgaven.</div>'}
        </div>
      </div>
    </div>`;
}

// -----------------------------------------------------------------
// Uitgaven — wrapper met sub-tab bar (Extra / Vaste / Variabele)
// -----------------------------------------------------------------

/**
 * Container voor de Uitgaven-tab. Toont bovenaan de sub-tab-bar
 * (Extra/Vaste/Variabele voor persoon; Vaste/Variabele voor samen)
 * en daaronder de content van de actieve sub-tab.
 *
 * De sub-tab-bar wist een eventueel open swipe-rij voor we van sub
 * wisselen (resetOpenRow zit in switchSubTab in render.js).
 */
export function buildUitgaven() {
  const isSamen = state.person === 'samen';

  // "Extra"-sub-tab pas tonen wanneer de persoon ergens al een extra-uitgave
  // heeft geboekt (via + Nieuwe transactie, NIET als "begroot" gemarkeerd).
  // Reden: tijdens initial setup heb je nog niks, en een lege Extra-tab leidt
  // alleen maar af. Eens je 'm gebruikt hebt verschijnt hij vanzelf.
  const hasExtras = !isSamen
    && Object.values(state.S[state.person]?.hist || {}).some(h => (h.ex || []).length > 0);

  const subs = isSamen
    ? SUBTABS_S
    : (hasExtras ? SUBTABS_P : SUBTABS_P.filter(s => s.id !== 'extra'));

  const active = state.activeSubTab;

  // Fallback: als activeSubTab niet bij deze persoon-modus past
  // (bv. 'extra' is verborgen of 'svast' overgeleverd uit samen),
  // val terug op de eerste beschikbare sub van deze modus.
  const safeActive = subs.some(s => s.id === active) ? active : subs[0].id;

  // State herstellen wanneer we naar fallback grepen, zodat een volgende
  // render (bv. nadat de Extra-tab pas verschijnt na een nieuwe extra-boeking)
  // niet plots de gebruiker uit "Vaste" naar "Extra" knalt.
  if (safeActive !== active) state.activeSubTab = safeActive;

  const subBar = `<div class="subtabs">
    ${subs.map(s => `<button class="subtab${s.id === safeActive ? ' active' : ''}"
        data-subtab-id="${s.id}"
        onclick="switchSubTab('${s.id}')">${s.lbl}</button>`).join('')}
  </div>`;

  let body = '';
  if (safeActive === 'extra')    body = buildExtra();
  else if (safeActive === 'vast')     body = buildItemList('vast', state.person);
  else if (safeActive === 'variabel') body = buildItemList('var',  state.person);
  else if (safeActive === 'svast')    body = buildItemList('vast', 'samen');
  else if (safeActive === 'svar')     body = buildItemList('var',  'samen');

  return subBar + `<div class="subpanel">${body}</div>`;
}

// -----------------------------------------------------------------
// Vaste / variabele kosten — for both personal accounts and Samen
// -----------------------------------------------------------------

export function buildItemList(type, p) {
  const d = state.S[p];
  const arr = type === 'vast' ? d.vast : d.var;
  const isSamen = p === 'samen';
  const h = isSamen ? null : hist();
  const ph = isSamen ? null : prevHist();

  // Sum of extra-expenses linked to each item, current vs. previous month.
  const eThis = {};
  const ePrev = {};
  if (!isSamen) {
    (h.ex || []).forEach(e => { if (e.vr != null) eThis[e.vr] = (eThis[e.vr] || 0) + e.a; });
    (ph.ex || []).forEach(e => { if (e.vr != null) ePrev[e.vr] = (ePrev[e.vr] || 0) + e.a; });
  }

  const tot = arr.reduce((s, i) => s + ma(i), 0);

  // Progress bar (50/30/20 rule) for personal accounts only. Doel = % van vast loon.
  let rbar = '';
  const loon = isSamen ? 0 : vastLoon(p);
  if (!isSamen && loon > 0) {
    const tgt = loon * (type === 'vast' ? 0.5 : 0.3);
    const pct = Math.min(110, Math.round(tot / tgt * 100));
    const c = pct > 100 ? '#c0392b' : (type === 'vast' ? '#5c6bc0' : '#e67e22');
    rbar = `<div class="card" style="margin-bottom:.85rem">
      <div class="card-title">${type === 'vast' ? 'Vaste kosten — max 50% van vast loon' : 'Variabele kosten — max 30% van vast loon'}</div>
      <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--color-text-secondary);margin-bottom:3px">
        <span>€${Math.round(tot)} gebruikt van €${Math.round(tgt)} max</span>
        <span style="color:${c};font-weight:500">${pct}%${pct > 100 ? ' ⚠️ over max' : ''}</span>
      </div>
      <div class="rbar-bg"><div class="rbar-fill" style="width:${Math.min(100, pct)}%;background:${c}"></div></div>
      <div style="font-size:12px;color:var(--color-text-secondary);margin-top:4px">
        ${Math.round(tot / loon * 100)}% van je vast loon (€${Math.round(loon)})
      </div>
    </div>`;
  }

  // Bepaal welke items als "duur" gemarkeerd worden — alleen wanneer de
  // bespaar-modus actief is op de huidige persoon (over 50% of 30%).
  const bsActive = !isSamen && bespaarStatus(p).bespaarModus;
  const expensiveIds = new Set();
  if (bsActive) {
    expensiveItems(p, 5).filter(it => it.__type === type)
      .forEach(it => expensiveIds.add(it.__i));
  }

  const key = type + p;
  const rows = arr.map((item, i) => {
    const ek = key + i;
    const open = state.editing[ek];
    const extra = eThis[i] || 0;
    const warn = !isSamen && type === 'var' && extra > 0 && (ePrev[i] || 0) > 0;
    const isExpensive = expensiveIds.has(i);

    const cmeta = catById(item.cat);
    const isAuto = !!item.auto;
    const isDom  = !!item.domicilie;

    // Per-item progressie-balkje. Twee varianten:
    //  - freqM === 1 (maandelijks): besteed-deze-cyclus / gepland
    //  - freqM > 1 (kwartaal/halfjaar/jaar/custom): sinking fund — wat is
    //    er sinds de laatste betaling opgebouwd?
    let barHTML = '';
    if (!isSamen && !isAuto) {
      const fM = freqM(item);
      if (fM === 1) {
        const planned   = ma(item);
        const rawSpent  = spentPerItem(p, type, i, curKey());
        const effective = isDom ? Math.max(planned, rawSpent) : rawSpent;
        const overshoot = Math.max(0, effective - planned);
        const pct       = planned > 0 ? Math.min(120, effective / planned * 100) : 0;
        const barColor  = overshoot > 0 ? '#c0392b' : accentColor();
        const metaTxt   = `€${effective.toFixed(2)} / €${planned.toFixed(2)} deze cyclus${overshoot > 0 ? ` · ⚠️ €${overshoot.toFixed(2)} over` : ''}`;
        barHTML = `<div class="ibar-row">
          <div class="ibar-bg"><div class="ibar-fill" style="width:${Math.min(100, pct)}%;background:${barColor}"></div></div>
          <div class="ibar-meta">${metaTxt}</div>
        </div>`;
      } else {
        const sf = sinkingFundFor(p, type, i, curKey());
        if (!sf.savingsActive) {
          // Opt-in voor sinking funds: zonder expliciete "ik spaar" toont
          // Florijn geen geprojecteerd bedrag (zou misleidend zijn — de
          // gebruiker heeft in werkelijkheid niks opzij gezet).
          barHTML = `<div class="ibar-row ibar-sf-off">
            <div class="ibar-meta">
              💤 Niet actief opzij aan 't zetten
              <button class="badd ibar-sf-btn" onclick="startSavingForItem('${type}','${p}',${i})">▶ Start vanaf nu</button>
            </div>
          </div>`;
        } else {
          // Rood alleen bij achterstallig (= er was wél een betaling, te lang geleden).
          const barColor = sf.overdue ? '#c0392b' : accentColor();
          const monthsLbl = `${Math.min(sf.elapsed, sf.months)}/${sf.months} mnd`;
          const metaTxt = sf.overdue
            ? `€${sf.total.toFixed(2)} verschuldigd — ${sf.elapsed - sf.months} mnd achterstallig`
            : `€${sf.saved.toFixed(2)} / €${sf.total.toFixed(2)} opzij · ${monthsLbl}`;
          barHTML = `<div class="ibar-row">
            <div class="ibar-bg"><div class="ibar-fill" style="width:${Math.min(100, sf.pct)}%;background:${barColor}"></div></div>
            <div class="ibar-meta">
              ${metaTxt}
              <button class="bedit ibar-sf-btn" title="Sparen pauzeren" onclick="stopSavingForItem('${type}','${p}',${i})">⏸</button>
            </div>
          </div>`;
        }
      }
    }

    const fM = freqM(item);
    const freqLbl = fM === 1
      ? 'Maandelijks'
      : fM === 3  ? `Kwartaal → €${ma(item).toFixed(2)}/mnd`
      : fM === 6  ? `Halfjaarlijks → €${ma(item).toFixed(2)}/mnd`
      : fM === 12 ? `Jaarlijks → €${ma(item).toFixed(2)}/mnd`
      : `Om de ${fM} mnd → €${ma(item).toFixed(2)}/mnd`;

    const rowHTML = `<div class="irow">
      <div class="iemoji" title="${cmeta.label}">${cmeta.emoji}</div>
      <div class="iinfo">
        <div class="iname">${item.n}${isAuto ? ' <span class="auto-tag">auto uit Samen</span>' : ''}${isDom ? ' <span class="dom-tag">🔁 domiciliëring</span>' : ''}${isExpensive ? ' <span class="duur-tag">🔴 duur</span>' : ''}${warn ? `<span class="wtag">${icon('alert-triangle', '', 9)} verhoog budget?</span>` : ''}</div>
        <div class="ifreq">${freqLbl}</div>
        ${barHTML}
        ${extra > 0 ? `<div class="iwarn">${icon('info-circle', '', 11)} €${extra.toFixed(2)} extra dit maand</div>` : ''}
      </div>
      <div class="iright">
        <div class="iamt">€${item.a.toFixed(2)}</div>
      </div>
    </div>`;

    // Frequentie-preset: match op huidige freqM (1/3/6/12) of 'custom'
    const curF = item.freqM ?? (item.f === 'j' ? 12 : 1);
    const isPreset = [1, 3, 6, 12].includes(curF);
    const editBox = open ? `<div class="edit-box">
      <label>Bedrag (€)</label>
      <input type="number" id="ei-${ek}" value="${item.a.toFixed(2)}" step="0.01">
      <label>Categorie</label>
      <select id="ec-${ek}">
        ${catsForScope('uit').filter(c => !['sparen', 'extra_sparen', 'terugbet_uit'].includes(c.id)).map(c => `<option value="${c.id}" ${c.id === item.cat ? 'selected' : ''}>${c.emoji} ${c.label}</option>`).join('')}
      </select>
      <label>Frequentie</label>
      <select id="ef-${ek}" onchange="onFreqChange(this, 'efc-${ek}', 'elp-wrap-${ek}')">
        <option value="1"  ${isPreset && curF === 1  ? 'selected' : ''}>Maandelijks</option>
        <option value="3"  ${isPreset && curF === 3  ? 'selected' : ''}>Kwartaal (om de 3 maanden)</option>
        <option value="6"  ${isPreset && curF === 6  ? 'selected' : ''}>Halfjaarlijks (om de 6 maanden)</option>
        <option value="12" ${isPreset && curF === 12 ? 'selected' : ''}>Jaarlijks</option>
        <option value="custom" ${!isPreset ? 'selected' : ''}>Anders…</option>
      </select>
      <div id="efc-${ek}" style="display:${!isPreset ? 'block' : 'none'};margin-top:4px;font-size:12px;color:var(--color-text-secondary)">
        Om de <input type="number" id="efcN-${ek}" min="1" max="60" value="${!isPreset ? curF : 2}" style="width:60px"> maanden
      </div>
      <div id="elp-wrap-${ek}" style="display:${curF !== 1 ? 'block' : 'none'};margin-top:6px;font-size:12px;color:var(--color-text-secondary)">
        <label style="display:block;margin-bottom:3px">Laatst betaald (optioneel)</label>
        <input type="month" id="elp-${ek}" value="${cycleKeyToMonthInput(item.lastPaidCycle)}" style="font-size:13px">
      </div>
      <div style="margin-top:10px;padding:10px 12px;background:var(--color-background-secondary);border-radius:6px">
        <div style="font-size:12px;color:var(--color-text-secondary);font-weight:500;margin-bottom:6px">Betaalwijze</div>
        <label class="cb-row cb-block">
          <input type="checkbox" id="ed-${ek}" ${item.domicilie ? 'checked' : ''}>
          <span>🔁 Automatische afschrijving (domiciliëring)</span>
        </label>
        <div style="font-size:11px;color:var(--color-text-secondary);margin:4px 0 0 24px">
          Aangevinkt: gaat automatisch van je rekening. Uitgevinkt: je betaalt zelf (overschrijving, cash, kaart).
        </div>
        <div style="margin-top:10px">
          <label style="display:block;font-size:12px;color:var(--color-text-secondary);margin-bottom:3px">Betaaldag <span style="font-weight:400">(1-31, optioneel)</span></label>
          <input type="number" id="ebd-${ek}" min="1" max="31" value="${item.betaalDag ?? ''}" placeholder="leeg = anker-dag ${ankerDay(p)}" style="width:100%;font-size:13px">
          <div style="font-size:11px;color:var(--color-text-secondary);margin-top:3px">
            Vooral relevant bij domiciliëring — dan weet de cashflow-prognose wanneer het bedrag wegvloeit.
          </div>
        </div>
      </div>
      <div class="ebtns">
        <button class="bcancel" onclick="toggleEdit('${ek}','${type}','${p}')">Annuleer</button>
        <button class="bsave"   onclick="saveEdit('${ek}','${type}','${p}',${i})">Opslaan</button>
      </div>
    </div>` : '';

    // Auto-items (Gezamenlijke kosten): geen swipe, geen edit
    const wrapped = isAuto
      ? rowHTML
      : wrapSwipe(rowHTML, {
          edit: `toggleEdit('${ek}','${type}','${p}')`,
          del:  `delItem('${type}','${p}',${i})`
        });

    return wrapped + editBox;
  }).join('') || `<div style="font-size:12px;color:var(--color-text-secondary);padding:6px 0">Nog niets toegevoegd.</div>`;

  const addkey = 'af-' + key;
  const infoId = 'info-' + key;

  const infoText = type === 'vast'
    ? `<strong>Vaste kosten</strong> zijn uitgaven die <em>moeten</em> gebeuren — dingen die je niet
       zomaar kan opzeggen zonder gevolgen. Denk aan huur, lening, verzekeringen, telefoonabonnement,
       internet, ziekenkas. Bedrag staat vast en de looptijd ligt vast.`
    : `<strong>Variabele kosten</strong> zijn alle andere terugkerende uitgaven — abonnementen die
       je kan opzeggen (Netflix, Streamz, ...), boodschappen, vervoer, hobby, uitgaan. Je voorziet
       er een budget voor, maar het bedrag of zelfs of je 't hebt kan variëren.`;

  // Abonnementen-jaarbedrag info-kaartje — alleen op Variabele kosten,
  // niet op Samen, alleen wanneer er werkelijk abonnementen zijn.
  let abonnCard = '';
  if (type === 'var' && !isSamen) {
    const ab = abonnementenJaar(p);
    if (ab.count > 0) {
      abonnCard = `<div class="card" style="margin-bottom:.85rem">
        <div class="card-title">🎟️ Abonnementen</div>
        <div style="font-size:13px;color:var(--color-text-primary)">
          ${ab.count} abonnement${ab.count === 1 ? '' : 'en'} samen:
          <strong>€${ab.jaar.toFixed(0)}/jaar</strong>
          <span style="color:var(--color-text-secondary)">(€${ab.monthly.toFixed(0)}/mnd)</span>
        </div>
      </div>`;
    }
  }

  return rbar + abonnCard + `<div class="card">
    <div class="card-title">
      <span>${type === 'vast' ? 'Vaste kosten' : 'Variabele kosten'}
        <button class="info-btn" title="Wat valt hieronder?" onclick="toggleForm('${infoId}')">i</button>
      </span>
      <button class="addtoggle" onclick="openAddItem('${type}','${p}')">+ Toevoegen</button>
    </div>
    <div class="info-block" id="${infoId}" style="display:none">${infoText}</div>
    ${rows}
    <div class="trow">
      <span class="tlbl">Totaal/maand</span>
      <span class="tamt">€${tot.toFixed(2)}</span>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Inkomsten — vaste maandelijkse/jaarlijkse inkomsten + anker-keuze
// -----------------------------------------------------------------

/** Inline bewerk-form voor één variabele-inkomst (hist.in entry). */
function buildVarInkEditForm(e, i) {
  let isoDate = '';
  if (e.date) {
    const m = String(e.date).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) isoDate = `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
  }
  const cats = catsForScope('in').filter(c => !['sparen', 'rest_vorig', 'terugbet_uit'].includes(c.id));
  const catOpts = cats.map(c =>
    `<option value="${c.id}"${c.id === e.cat ? ' selected' : ''}>${c.emoji} ${c.label}</option>`
  ).join('');

  return `<div class="edit-box">
    <div class="field">
      <label>Bedrag</label>
      <input id="vin-a-${i}" type="number" step="0.01" min="0.01" value="${e.a}">
    </div>
    <div class="field">
      <label>Categorie</label>
      <select id="vin-c-${i}">${catOpts}</select>
    </div>
    <div class="field">
      <label>Omschrijving</label>
      <input id="vin-d-${i}" type="text" value="${(e.desc || '').replace(/"/g, '&quot;')}">
    </div>
    <div class="field">
      <label>Datum</label>
      <input id="vin-dt-${i}" type="date" value="${isoDate}">
    </div>
    <div class="btn-row" style="display:flex;gap:6px;margin-top:8px">
      <button class="bsave" onclick="saveVarInk(${i})">Opslaan</button>
      <button class="bcancel" onclick="toggleEditVarInk(${i})">Annuleren</button>
    </div>
  </div>`;
}

export function buildInkomsten() {
  const d = state.S[state.person];
  const list = d.inkomsten || [];
  const key = 'inkomstenadd';

  const totMaand = list.reduce((s, i) => s + (i.f === 'j' ? i.a / 12 : i.a), 0);

  // Pie-segmenten — toont % per bron van binnenkomend geld.
  const seg = inkomstenSegs(state.person);
  const tot = seg.totaal || 1;
  const pieCard = `<div class="card" style="margin-bottom:.85rem">
    <div class="card-title">Verdeling inkomsten</div>
    <div class="pie-wrap">
      <canvas id="pieInkomsten" width="130" height="130"></canvas>
      <div class="pie-leg">
        ${seg.cats.length
          ? themeRecolor(seg.cats).map(c => `
              <div class="lrow">
                <div class="ldot" style="background:${c.color}"></div>
                <span class="llbl">${c.emoji} ${c.label}</span>
                <span class="lpct">${Math.round(c.amount / tot * 100)}%</span>
              </div>`).join('')
          : '<div style="font-size:12px;color:var(--color-text-secondary)">Nog geen inkomsten deze cyclus.</div>'}
      </div>
    </div>
  </div>`;

  // Variabele inkomsten — losse hist.in entries deze cyclus.
  const h = histFor(state.person);
  const varIn = h.in || [];
  const varTot = varIn.reduce((s, e) => s + e.a, 0);
  const varRows = varIn.length
    ? varIn.map((e, idx) => {
        if (state.editing['varin-' + idx]) return buildVarInkEditForm(e, idx);

        const cmeta = catById(e.cat);
        const rowHTML = `<div class="txrow">
          <div class="txico" style="background:${cmeta.color}22">${cmeta.emoji}</div>
          <div class="txinfo">
            <div class="txname">${e.desc || cmeta.label}</div>
            <div class="txmeta">${e.date} · ${cmeta.label}</div>
          </div>
          <div class="txamt" style="color:${accentColor()}">+€${e.a.toFixed(2)}</div>
        </div>`;
        return wrapSwipe(rowHTML, {
          edit: `toggleEditVarInk(${idx})`,
          del:  `delVarInk(${idx})`
        });
      }).join('')
    : `<div style="font-size:12px;color:var(--color-text-secondary);padding:6px 0">Geen variabele inkomsten deze cyclus.</div>`;
  const varCard = `<div class="card" style="margin-bottom:.85rem">
    <div class="card-title">Variabele inkomsten — deze cyclus</div>
    ${varRows}
    ${varIn.length ? `<div class="trow"><span class="tlbl">Totaal</span><span class="tamt">€${varTot.toFixed(2)}</span></div>` : ''}
  </div>`;

  const rows = list.map((i, idx) => {
    const cmeta = catById(i.cat);
    const ek = 'ink' + idx;
    const open = state.editing[ek];
    const rowHTML = `<div class="irow">
      <div class="iemoji" title="${cmeta.label}">${cmeta.emoji}</div>
      <div class="iinfo">
        <div class="iname">${i.n}${i.anker ? ' <span class="anker-badge">⚓ anker</span>' : ''}</div>
        <div class="ifreq">
          ${i.f === 'j' ? 'Jaarlijks → €' + (i.a / 12).toFixed(2) + '/mnd' : 'Maandelijks'}
          · dag ${i.startDag || 1}
        </div>
      </div>
      <div class="iright">
        <div class="iamt">€${i.a.toFixed(2)}</div>
      </div>
    </div>`;

    const editBox = open ? `<div class="edit-box">
      <label>Bedrag (€)</label>
      <input type="number" id="ein-a-${idx}" value="${i.a.toFixed(2)}" step="0.01">
      <label>Categorie</label>
      <select id="ein-cat-${idx}">
        ${catsForScope('in').filter(c => !['extra_sparen', 'rest_vorig', 'uit_spaar'].includes(c.id)).map(c => `<option value="${c.id}" ${c.id === i.cat ? 'selected' : ''}>${c.emoji} ${c.label}</option>`).join('')}
      </select>
      <label>Startdag van de maand (1–28)</label>
      <input type="number" id="ein-sd-${idx}" value="${i.startDag || 1}" min="1" max="28">
      <label class="cb-row cb-block cb-small">
        <input type="checkbox" id="ein-anker-${idx}" ${i.anker ? 'checked' : ''}>
        <span>Anker — dit loon bepaalt de budgetmaand</span>
      </label>
      <div class="ebtns">
        <button class="bcancel" onclick="toggleEditInk(${idx})">Annuleer</button>
        <button class="bsave"   onclick="saveInk(${idx})">Opslaan</button>
      </div>
    </div>` : '';

    return wrapSwipe(rowHTML, {
      edit: `toggleEditInk(${idx})`,
      del:  `delInk(${idx})`
    }) + editBox;
  }).join('') || `<div style="font-size:12px;color:var(--color-text-secondary);padding:6px 0">Nog geen inkomsten toegevoegd.</div>`;

  return pieCard + `<div class="card">
    <div class="card-title">
      Vaste inkomsten
      <button class="addtoggle" onclick="openAddItem('inkomst')">+ Toevoegen</button>
    </div>
    ${rows}
    <div class="trow">
      <span class="tlbl">Totaal/maand</span>
      <span class="tamt">€${totMaand.toFixed(2)}</span>
    </div>
  </div>` + varCard;
}

// -----------------------------------------------------------------
// Extra — list of one-off, off-budget expenses
// -----------------------------------------------------------------

/** Inline bewerk-form voor één extra-entry — bedrag, omschrijving, cat, datum.
 *  Wordt gerenderd i.p.v. de gewone rij zolang state.editing['extra-'+i] true is. */
function buildExtraEditForm(e, i) {
  // 'D/M/YYYY' → 'YYYY-MM-DD' voor het <input type="date">-veld.
  let isoDate = '';
  if (e.date) {
    const m = String(e.date).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) isoDate = `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
  }
  const cats = (state.S.cats || []).filter(c => c.scope === 'uit');
  const catOpts = cats.map(c =>
    `<option value="${c.id}"${c.id === e.cat ? ' selected' : ''}>${c.emoji} ${c.label}</option>`
  ).join('');

  // Begroot-koppeling: lijst van planned items met dezelfde cat als de entry,
  // zodat de gebruiker een vergeten link alsnog kan zetten of weghalen.
  const d = state.S[state.person] || {};
  const matches = [
    ...(d.vast || []).map((it, idx) => ({ ...it, __type: 'vast', __i: idx }))
                     .filter(it => it.cat === e.cat),
    ...(d.var  || []).map((it, idx) => ({ ...it, __type: 'var',  __i: idx }))
                     .filter(it => it.cat === e.cat)
  ];
  const linkOpts = `
    <option value="">— Niet begroot —</option>
    ${matches.map(it => {
      const key = `${it.__type}:${it.__i}`;
      const lbl = `${it.__type === 'vast' ? 'Vast' : 'Variabel'}: ${it.n} (€${Number(it.a).toFixed(2)})`;
      return `<option value="${key}"${e.vrKey === key ? ' selected' : ''}>${lbl}</option>`;
    }).join('')}
  `;

  return `<div class="edit-box">
    <div class="field">
      <label>Bedrag</label>
      <input id="ex-a-${i}" type="number" step="0.01" min="0.01" value="${e.a}">
    </div>
    <div class="field">
      <label>Categorie</label>
      <select id="ex-c-${i}">${catOpts}</select>
    </div>
    <div class="field">
      <label>Omschrijving</label>
      <input id="ex-d-${i}" type="text" value="${(e.desc || '').replace(/"/g, '&quot;')}">
    </div>
    <div class="field">
      <label>Datum</label>
      <input id="ex-dt-${i}" type="date" value="${isoDate}">
    </div>
    <div class="field">
      <label>Gekoppeld aan</label>
      <select id="ex-vr-${i}">${linkOpts}</select>
    </div>
    <div class="btn-row" style="display:flex;gap:6px;margin-top:8px">
      <button class="bsave" onclick="saveExtra(${i})">Opslaan</button>
      <button class="bcancel" onclick="toggleEditExtra(${i})">Annuleren</button>
    </div>
  </div>`;
}

export function buildExtra() {
  const h = hist();
  const items = h.ex || [];

  const rows = items.length
    ? items.map((e, i) => {
        // Inline edit-form als state.editing dit element open heeft.
        if (state.editing['extra-' + i]) return buildExtraEditForm(e, i);

        const c = catById(e.cat);
        const bronTag = e.vanSamen
          ? ' <span class="bron-tag bron-samen">geleend van Samen</span>'
          : e.vanSpaar
          ? ' <span class="bron-tag bron-spaar">uit spaarpot</span>'
          : '';
        const rowHTML = `<div class="txrow">
        <div class="txico" style="background:${c.color}22">${c.emoji}</div>
        <div class="txinfo">
          <div class="txname">${e.desc || c.label}${bronTag}</div>
          <div class="txmeta">${e.date} · ${c.label}</div>
        </div>
        <div class="txamt">-€${e.a.toFixed(2)}</div>
      </div>`;
        return wrapSwipe(rowHTML, {
          edit: `toggleEditExtra(${i})`,
          del:  `delExtra(${i})`
        });
      }).join('')
    : `<div style="font-size:12px;color:var(--color-text-secondary);padding:6px 0">Geen extra uitgaven 🎉</div>`;

  const tot = items.reduce((s, e) => s + e.a, 0);

  const infoText = `<strong>Extra kosten</strong> zijn uitgaven die <em>niet in je vaste of variabele
   planning</em> staan — eenmalige aankopen, onverwachte uitgaven, cadeaus, een spontaan etentje.
   Alles wat je via de + Nieuwe transactie-knop toevoegt en NIET als "begroot" markeert komt hier
   terecht. Hoe minder hier hoeft te staan, hoe beter je planning klopt.`;

  return `<div class="card">
    <div class="card-title">
      <span>Extra — niet begroot
        <button class="info-btn" title="Wat valt hieronder?" onclick="toggleForm('info-extra')">i</button>
      </span>
    </div>
    <div class="info-block" id="info-extra" style="display:none">${infoText}</div>
    ${rows}
    <div class="trow">
      <span class="tlbl">Totaal extra</span>
      <span class="tamt">€${tot.toFixed(2)}</span>
    </div>
  </div>`;
}

// -----------------------------------------------------------------
// Dromen — savings goals with % allocation
// -----------------------------------------------------------------

export function buildDromen() {
  const d = state.S[state.person];
  const loon  = vastLoon(state.person);
  const tgt   = loon * 0.2;
  // Werkelijk dit-cycle gestort = som van hist.ex met cat='sparen'.
  const actual = spentInCycle(state.person, 'sparen');
  const pct = tgt > 0 ? Math.min(110, Math.round(actual / tgt * 100)) : 0;
  const c = accentColor();
  // totPct = som van zichtbare handmatige %'s (alleen voor de "ideaal 100%"-warning).
  // totWeight = som van échte verdelings-gewichten (paused → 0, deadline → effectivePct);
  // dit is wat we tonen als "krijgt €X/mnd" en wat distributie gebruikt.
  const totPct = d.dromen.reduce((s, dr) => s + (dr.paused ? 0 : (dr.pct || 0)), 0);
  const totWeight = d.dromen.reduce((s, dr) => s + droomWeight(dr, state.person), 0);

  // Spaarquote dit jaar (Fase E) — één getal, geen grafiek. Verschijnt
  // alleen als er een vast loon én iets gestort is dit jaar.
  const sq = spaarquoteJaar(state.person);
  const quoteLine = (sq.basis > 0 && sq.spaar > 0)
    ? `<div style="font-size:12px;color:var(--color-text-secondary);margin-top:6px;padding-top:6px;border-top:1px solid var(--color-border-tertiary)">
        Spaarquote ${sq.year}: <strong style="color:var(--color-text-primary)">${sq.pct.toFixed(1).replace('.', ',')}%</strong> van vast loon (€${Math.round(sq.spaar)} gestort dit jaar)
      </div>`
    : '';

  const rbar = `<div class="card" style="margin-bottom:.85rem">
    <div class="card-title">Voortgang — sparen (doel 20% van vast loon)</div>
    <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--color-text-secondary);margin-bottom:3px">
      <span>€${actual.toFixed(2)} gestort / €${tgt.toFixed(2)} doel</span>
      <span style="color:${c};font-weight:500">${pct}%</span>
    </div>
    <div class="rbar-bg"><div class="rbar-fill" style="width:${Math.min(100, pct)}%;background:${c}"></div></div>
    <div style="font-size:12px;color:var(--color-text-secondary);margin-top:4px">
      ${loon > 0 ? `20% van vast loon €${Math.round(loon)} = €${tgt.toFixed(2)}/mnd` : 'Geen vast loon (anker) ingesteld'}
    </div>
    ${quoteLine}
  </div>`;

  // Noodfonds-mijlpaal (Fase B). Twee toestanden:
  //  a) Geen droom gevlagd, wel dromen → banner met markeer-keuzes.
  //  b) Wel een noodfonds → eigen mijlpaal-kaart bovenaan, underMinimum-notif.
  const nf = noodfondsStatus(state.person);
  const vastTotaal = (d.vast || []).reduce((s, it) => s + ma(it), 0);
  let noodBanner = '';
  let noodCard   = '';

  // Snapshot-banner: tijdelijk100Noodfonds() bewaart de originele pct's.
  // Zolang die snapshot er staat, krijgt de gebruiker een opvallende
  // herstel-knop bovenaan Dromen — geen verloren-pct-paniek.
  // Snapshot kan {} of null zijn na hydrate — alleen banner tonen wanneer
  // er effectief pre-burst-pcts in zitten om naar terug te keren.
  const snap = d.noodfondsPctSnapshot;
  const restoreBanner = (snap && Object.keys(snap).length > 0)
    ? `<div class="notif nf-blue" style="margin-bottom:.85rem">
        <div class="ntitle">🛡️ Andere dromen staan tijdelijk op 0%</div>
        <div class="nsub">Je spaarcenten gaan momenteel volledig naar het noodfonds. Klik "Herstel verdeling" om de eerdere verdeling terug te zetten.</div>
        <div class="nbtns">
          <button class="nbtn ok" onclick="herstelDroomVerdeling()">Herstel verdeling</button>
        </div>
      </div>`
    : '';
  if (nf.droom) {
    const nfIdx = d.dromen.indexOf(nf.droom);
    const dr = nf.droom;
    const fillPct = Math.min(100, Math.round((dr.gesp / dr.doel) * 100));
    const monthsCovered = vastTotaal > 0
      ? (dr.gesp / vastTotaal).toFixed(1).replace('.', ',') + ' mnd'
      : '— geen vaste lasten';
    const suggLine = vastTotaal > 0
      ? `<div style="font-size:11px;color:var(--color-text-secondary);margin-top:3px">Suggestie doel: €${Math.round(vastTotaal * 3)} (= 3 × €${vastTotaal.toFixed(0)}/mnd vaste lasten)</div>`
      : '';
    // Alleen de 100%-knop tonen als de burst NIET al loopt — anders is 'm
     // redundant (de restoreBanner bovenaan dekt al de "schakel uit"-actie).
    const burstActive = snap && Object.keys(snap).length > 0;
    const underMinBlock = (nf.underMinimum && !burstActive)
      ? `<div class="notif nf-red" style="margin-top:8px;margin-bottom:0">
          <div class="ntitle">⚠️ Onder 1 maand vaste lasten</div>
          <div class="nsub">Je noodfonds dekt minder dan één maand. Overweeg tijdelijk meer spaarcenten naar het noodfonds te sturen.</div>
          <div class="nbtns">
            <button class="nbtn ok" onclick="tijdelijk100Noodfonds()">Tijdelijk 100% naar noodfonds</button>
          </div>
        </div>`
      : '';
    noodCard = `<div class="card noodfonds-card" style="margin-bottom:.85rem">
      <div class="noodfonds-lbl">🛡️ Noodfonds — mijlpaal</div>
      <div class="dhead">
        <span class="dname">${dr.n}</span>
        <span class="dmeta">€${Math.round(dr.gesp)} / €${dr.doel}</span>
      </div>
      <div class="dbarbg"><div class="dbar" style="width:${fillPct}%;background:${dr.c}"></div></div>
      <div class="dsub">${fillPct}% gespaard · dekt ${monthsCovered} vaste lasten</div>
      ${suggLine}
      <div class="pctrow" style="margin-top:8px">
        <label style="font-size:11px;color:var(--color-text-secondary)">% spaarcenten:</label>
        <input type="number" value="${dr.pct}" min="0" max="100" style="max-width:55px;font-size:12px;margin-left:4px" onchange="setPct(${nfIdx},this.value)">
        <span style="font-size:11px;color:var(--color-text-secondary)">%</span>
        <button class="bcancel" style="margin-left:auto;padding:4px 10px;font-size:11px" onclick="unmarkNoodfonds()" title="Maak dit weer een gewone droom, geen mijlpaal-status meer">Niet meer mijn noodfonds</button>
      </div>
      <div style="font-size:11px;color:var(--color-text-secondary);margin-top:6px">Het noodfonds kun je niet pauzeren — het blijft altijd voorrang krijgen.</div>
      ${dr.gesp > 0 ? `<button class="binnen" style="width:100%;margin-top:8px;padding:7px 12px" onclick="aanspreekNoodfonds()">🛟 Noodfonds aanspreken — bij inkomensgat of nood</button>` : ''}
      ${underMinBlock}
    </div>`;
  } else if (d.dromen.length) {
    const opts = d.dromen.map((dr, i) =>
      `<button class="nbtn" onclick="markAlsNoodfonds(${i})">${dr.n}</button>`
    ).join('');
    noodBanner = `<div class="notif nf-blue">
      <div class="ntitle">💡 Markeer één droom als noodfonds</div>
      <div class="nsub">Krijgt voorrang en eigen voortgangs-tracking (mijlpaal).</div>
      <div class="nbtns">${opts}</div>
    </div>`;
  }

  // Reguliere dromen-lijst: noodfonds-droom toont al boven, dus skip 'm hier.
  const regularDromen = d.dromen.filter(dr => !dr.isNoodfonds);
  const drows = regularDromen.length
    ? regularDromen.map((dr) => {
        const i = d.dromen.indexOf(dr);
        // Bewerk-modus: één droom in edit, rest blijft normaal. Edit-form
        // toont álle velden tegelijk (incl. naam/doel/gespaard/kleur) zodat
        // de gebruiker niet meer hoeft te raden hoe % en deadline samenwerken.
        if (state.editing['droom-' + i]) {
          return `<div class="ditem dedit">
            <div class="dhead" style="margin-bottom:6px">
              <input type="text" id="edn-${i}" value="${dr.n}" placeholder="Naam" style="flex:1;font-size:13px;font-weight:500">
              <span class="dmeta" style="margin-left:8px">Bewerken</span>
            </div>
            <div class="addrow">
              <input type="number" id="edd-${i}" value="${dr.doel}" placeholder="Doelbedrag €" step="1">
              <input type="number" id="edg-${i}" value="${dr.gesp}" placeholder="Al gespaard €" step="1">
            </div>
            <div class="pctrow" style="margin-top:6px">
              <label style="font-size:11px;color:var(--color-text-secondary)">% spaarcenten:</label>
              <input type="number" id="edp-${i}" value="${dr.pct || 0}" min="0" max="100" style="max-width:60px;font-size:13px;margin-left:4px">
              <span style="font-size:11px;color:var(--color-text-secondary)">%</span>
              ${dr.deadline ? `<span style="font-size:10px;color:#e67e22;margin-left:6px">(genegeerd zolang deadline aanstaat)</span>` : ''}
            </div>
            <div class="pctrow" style="margin-top:6px">
              <label style="font-size:11px;color:var(--color-text-secondary)">Deadline:</label>
              <input type="date" id="edl-${i}" value="${dr.deadline || ''}" style="font-size:12px;margin-left:4px">
              <span style="font-size:10px;color:var(--color-text-secondary);margin-left:6px">(leeg = geen deadline)</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;margin-top:6px">
              <input type="color" id="edc-${i}" value="${dr.c}" style="width:32px;height:26px;border:none;cursor:pointer;padding:0;border-radius:5px">
              <label style="font-size:11px;color:var(--color-text-secondary)">Kleur</label>
            </div>
            <div style="display:flex;gap:6px;margin-top:8px">
              <button class="badd" onclick="saveDroom(${i})">Opslaan</button>
              <button class="bcancel" onclick="toggleEditDroom(${i})">Annuleer</button>
            </div>
          </div>`;
        }
        const p = Math.min(100, Math.round(dr.gesp / dr.doel * 100));
        const w = droomWeight(dr, state.person);
        // Share = wat deze droom maandelijks krijgt uit de spaarpot, volgens
        // het werkelijke gewicht (paused → 0, deadline → auto-berekend).
        const share = totWeight > 0 ? Math.round(tgt * w / totWeight) : 0;
        const ePct = effectivePct(dr, state.person);

        // Deadline-info: toont nodig/mnd + maanden tot deadline. Verschijnt
        // alleen als de droom een deadline heeft.
        let deadlineLine = '';
        if (dr.deadline) {
          const ml = deadlineMonthsLeft(dr);
          const need = deadlineMaandbedrag(dr);
          const dl = new Date(dr.deadline);
          const dlStr = !isNaN(dl.getTime())
            ? dl.getDate() + ' ' + MONTHS[dl.getMonth()].toLowerCase() + ' ' + dl.getFullYear()
            : '';
          if (dr.gesp >= dr.doel) {
            deadlineLine = `<div class="ddead">🎯 Deadline ${dlStr} — doel bereikt</div>`;
          } else if (ml === 0) {
            deadlineLine = `<div class="ddead ddead-rood">⏰ Deadline ${dlStr} — nu/voorbij · nog €${Math.round(need)} nodig</div>`;
          } else {
            const overshoot = need > spaarbedrag(state.person);
            deadlineLine = `<div class="ddead${overshoot ? ' ddead-rood' : ''}">🎯 Deadline ${dlStr} · ${ml} mnd · €${Math.round(need)}/mnd nodig${overshoot ? ' (overschrijdt spaarbedrag)' : ''}</div>`;
          }
        }

        // Projectie-regel onder de bestaande dsub.
        let projLine;
        if (dr.gesp >= dr.doel) {
          projLine = `<div class="dproj">Doel bereikt 🎉</div>`;
        } else if (dr.paused) {
          projLine = `<div class="dproj">⏸ Op pauze — krijgt nu niets</div>`;
        } else if (share <= 0) {
          projLine = `<div class="dproj">Geen tempo (0% spaarcenten)</div>`;
        } else {
          const proj = doelProjectie(dr, share);
          if (proj) {
            const dStr = proj.getDate() + ' ' + MONTHS[proj.getMonth()].toLowerCase() + ' ' + proj.getFullYear();
            projLine = `<div class="dproj">Bij dit tempo: ${dStr}</div>`;
          } else {
            projLine = '';
          }
        }

        // Sub-tekst: bij deadline tonen we het auto-berekende %, anders
        // het handmatig ingestelde dr.pct (zoals voorheen).
        const pctLabel = dr.deadline
          ? `auto ${ePct}% van spaarcenten`
          : `${dr.pct}% van spaarcenten`;
        const subLine = dr.paused
          ? `<div class="dsub">${p}% gespaard · pauze actief</div>`
          : `<div class="dsub">${p}% gespaard · +€${share}/mnd (${pctLabel})</div>`;

        // Manueel %-veld verbergen als deadline aanstaat (% volgt deadline).
        const pctInputRow = dr.deadline
          ? `<div class="pctrow">
              <label style="font-size:11px;color:var(--color-text-secondary)">Deadline:</label>
              <input type="date" id="dl-${i}" value="${dr.deadline}" style="font-size:12px;margin-left:4px" onchange="setDeadline(${i})">
              <button class="bcancel" style="margin-left:6px;padding:4px 8px;font-size:11px" onclick="document.getElementById('dl-${i}').value='';setDeadline(${i})" title="Deadline weghalen">×</button>
              ${dr.gesp > 0 ? `<button class="binnen" style="margin-left:auto" onclick="innenDroom(${i})">🎉 Geïnd!</button>` : ''}
            </div>`
          : `<div class="pctrow">
              <label style="font-size:11px;color:var(--color-text-secondary)">% spaarcenten:</label>
              <input type="number" value="${dr.pct}" min="0" max="100" style="max-width:55px;font-size:12px;margin-left:4px" onchange="setPct(${i},this.value)">
              <span style="font-size:11px;color:var(--color-text-secondary)">%</span>
              <input type="date" id="dl-${i}" value="" style="font-size:11px;margin-left:8px;max-width:135px" onchange="setDeadline(${i})" title="Deadline instellen">
              ${dr.gesp > 0 ? `<button class="binnen" style="margin-left:auto" onclick="innenDroom(${i})">🎉 Geïnd!</button>` : ''}
            </div>`;

        const front = `<div class="ditem${dr.paused ? ' dpaused' : ''}">
          <div class="dhead">
            <span class="dname">${dr.n}${dr.paused ? ' <span class="dpause-tag">⏸ pauze</span>' : ''}</span>
            <span class="dmeta">€${Math.round(dr.gesp)} / €${dr.doel}</span>
          </div>
          <div class="dbarbg"><div class="dbar" style="width:${p}%;background:${dr.c}"></div></div>
          ${subLine}
          ${deadlineLine}
          ${projLine}
          ${pctInputRow}
        </div>`;

        return wrapSwipe(front, {
          edit: `toggleEditDroom(${i})`,
          pause: `togglePause(${i})`,
          pauseLabel: dr.paused ? 'Hervatten' : 'Pauze',
          pauseIcon:  dr.paused ? '▶' : '⏸',
          del: `delDroom(${i})`
        });
      }).join('') +
      (totPct !== 100
        ? `<div style="font-size:11px;color:#e67e22;margin-top:4px">⚠️ Totaal: ${totPct}% — ideaal 100%</div>`
        : '')
    : `<div style="font-size:12px;color:var(--color-text-secondary);padding:6px 0">Nog geen reguliere dromen.</div>`;

  return rbar + restoreBanner + noodBanner + noodCard + `
    <div class="spaarcard">
      <div>
        <div style="font-size:11px;color:var(--color-text-secondary);margin-bottom:2px">Spaarbedrag/maand (20% van vast loon)</div>
        <div class="spaarval">€${tgt.toFixed(2)}</div>
      </div>
    </div>
    <div class="card">
      <div class="card-title">
        Spaardoelen
        <button class="addtoggle" onclick="openAddItem('droom')">+ Nieuw doel</button>
      </div>
      ${drows}
    </div>
    ${buildPensioenCard()}`;
}

/**
 * Pensioensparen-tegel (Fase D — BE). Verschijnt onderaan Dromen voor
 * persoonlijke views; toont status voor het lopende jaar + fiscaal voordeel.
 */
function buildPensioenCard() {
  if (state.person === 'samen') return '';
  if (!state.S[state.person]?.pensioenActief) return '';
  const ps = pensioensparenStatus(state.person);
  const fillPct = Math.round(ps.pct);
  const ibtn = `<button class="info-btn" title="Wat is dit?" onclick="toggleForm('info-pensioen')">i</button>`;
  const restLine = ps.overGrens
    ? `<div class="dsub" style="color:#e67e22">Boven de fiscale grens — extra storting levert geen extra voordeel.</div>`
    : ps.rest > 0
      ? `<div class="dsub">Nog €${ps.rest.toFixed(0)} fiscaal voordelig — geeft ~€${(ps.rest * ps.voordeelPct).toFixed(0)} extra voordeel als je 't volstort.</div>`
      : '';
  return `<div class="card" style="margin-top:.85rem">
    <div class="card-title">🏛️ Pensioensparen ${ps.year} ${ibtn}</div>
    <div class="dhead">
      <span class="dname">Gestort dit jaar</span>
      <span class="dmeta">€${ps.gestort.toFixed(0)} / €${ps.grens}</span>
    </div>
    <div class="dbarbg"><div class="dbar" style="width:${Math.min(100, fillPct)}%;background:#f39c12"></div></div>
    <div class="dsub">${fillPct}% gestort · belastingvoordeel zo ver: ~€${ps.voordeel.toFixed(0)} (${Math.round(ps.voordeelPct * 100)}%)</div>
    ${restLine}
    <div class="info-block" id="info-pensioen" style="display:none;margin-top:8px">
      <strong>BE pensioensparen</strong> = fiscaal voordelig sparen voor je pensioen via een verzekering of fonds. Je krijgt een belastingvermindering tot een jaarlijks plafond (in te stellen via ⚙️ Instellingen).
      <br><br>
      <strong>Tip:</strong> niet storten = geld weggeven aan de fiscus. Zelfs een kleine maandelijkse storting maakt het verschil — als je €${ps.grens} stort dit jaar, krijg je ~€${(ps.grens * ps.voordeelPct).toFixed(0)} terug bij de aangifte.
      <br><br>
      Voeg een storting toe via "+ Nieuwe transactie" met categorie "Pensioensparen". Alle bedragen die je dit jaar zo boekt, tellen hier mee.
    </div>
  </div>`;
}

